// themePropertyRegistry.ts
// Single source of truth for all checkout theme properties.
// Phase 1: Purely additive — no existing files are modified.

// ============================================================================
// Types
// ============================================================================

export type ThemePropertyType =
  | 'color'
  | 'font'
  | 'fontWeight'
  | 'fontStyle'
  | 'fontSize'
  | 'size'
  | 'shadow'
  | 'alignment'
  | 'select'
  | 'toggle'
  | 'image'
  | 'text';

export type ThemePropertyCategory =
  | 'tokens'
  | 'layout'
  | 'product'
  | 'description'
  | 'checkout'
  | 'cards'
  | 'variants'
  | 'statusBadges'
  | 'ticketGroups'
  | 'fields'
  | 'pwyw'
  | 'discountCode'
  | 'payments'
  | 'priceBreakdown'
  | 'coverProcessingFees'
  | 'button'
  | 'trust'
  | 'confirmation'
  | 'labels';

export type CssVarTransform = 'direct' | 'px' | 'lineHeight' | 'shadow';

export interface CssMapping {
  cssVar: string;
  transform: CssVarTransform;
  note?: string;
  cssDefault?: string;
}

export interface ThemeProperty {
  path: string;
  cssProperty?: string;
  type: ThemePropertyType;
  label: string;
  description: string;
  category: ThemePropertyCategory;
  cssVars: CssMapping[];
  options?: OptionsRef | { value: string | number; label: string }[];
  defaultValue?: unknown;
  derived?: boolean;
  resetable?: boolean;
  fontFamilyField?: string;
  parentFontField?: string;
  // Values that make another field's value impossible, and what to write there
  // instead: { <this value>: { <other path>: <value> } }. Applied by the select
  // in Form.js, so the stored theme matches what the value forces on screen.
  setAlso?: Record<string, Record<string, unknown>>;
  uiMeta?: {
    picker?: 'hex';
    showAutoOption?: boolean;
    previewSize?: 'background';
    defaultLabel?: string;
  };
}

// ============================================================================
// Shared Option Arrays
// ============================================================================

export const THEME_OPTIONS = {
  fontSize: [
    { value: '', label: 'Auto' },
    { value: 10, label: '10px' },
    { value: 11, label: '11px' },
    { value: 12, label: '12px' },
    { value: 13, label: '13px' },
    { value: 14, label: '14px' },
    { value: 16, label: '16px' },
    { value: 18, label: '18px' },
    { value: 20, label: '20px' },
    { value: 24, label: '24px' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
    { value: 48, label: '48px' },
    { value: 64, label: '64px' },
  ],
  fontStyle: [
    { value: '', label: 'Auto' },
    { value: 'normal', label: 'Normal' },
    { value: 'italic', label: 'Italic' },
  ],
  borderWidth: [
    { value: '', label: 'Auto' },
    { value: 0, label: 'None' },
    { value: 1, label: '1px' },
    { value: 2, label: '2px' },
    { value: 4, label: '4px' },
    { value: 8, label: '8px' },
  ],
  borderStyle: [
    { value: '', label: 'Auto' },
    { value: 'solid', label: 'Solid' },
    { value: 'dashed', label: 'Dashed' },
    { value: 'dotted', label: 'Dotted' },
  ],
  shadow: [
    { value: '', label: 'Auto' },
    { value: 'none', label: 'None' },
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra large' },
  ],
  iconSize: [
    { value: '', label: 'Auto' },
    { value: 16, label: '16px' },
    { value: 20, label: '20px' },
    { value: 24, label: '24px' },
    { value: 28, label: '28px' },
  ],
  sizePx: [
    { value: '', label: 'Auto' },
    { value: 0, label: '0px' },
    { value: 2, label: '2px' },
    { value: 4, label: '4px' },
    { value: 8, label: '8px' },
    { value: 12, label: '12px' },
    { value: 16, label: '16px' },
    { value: 24, label: '24px' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
    { value: 48, label: '48px' },
    { value: 64, label: '64px' },
    { value: 80, label: '80px' },
    { value: 96, label: '96px' },
    { value: 128, label: '128px' },
  ],
  paddingPx: [
    { value: '', label: 'Auto' },
    { value: 0, label: '0px' },
    { value: 2, label: '2px' },
    { value: 4, label: '4px' },
    { value: 6, label: '6px' },
    { value: 8, label: '8px' },
    { value: 12, label: '12px' },
    { value: 16, label: '16px' },
    { value: 24, label: '24px' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
  ],
  logoSizePx: [
    { value: '', label: 'Auto' },
    { value: 16, label: '16px' },
    { value: 24, label: '24px' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
    { value: 48, label: '48px' },
    { value: 64, label: '64px' },
    { value: 80, label: '80px' },
    { value: 96, label: '96px' },
    { value: 128, label: '128px' },
  ],
  confirmationMarkSize: [
    { value: '', label: 'Auto' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
    { value: 48, label: '48px' },
    { value: 56, label: '56px' },
    { value: 64, label: '64px' },
    { value: 80, label: '80px' },
    { value: 96, label: '96px' },
  ],
  alignment: [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
  ],
  radius: [
    { value: 'none', label: 'None' },
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra large' },
    { value: 'full', label: 'Full' },
  ],
  indicatorShape: [
    { value: 'dot', label: 'Dot' },
    { value: 'tick', label: 'Tick' },
    { value: 'star', label: 'Star' },
  ],
  indicatorRadius: [
    { value: 0, label: 'None' },
    { value: 2, label: 'Small' },
    { value: 4, label: 'Medium' },
    { value: 8, label: 'Large' },
    { value: 9999, label: 'Full' },
  ],
  indicatorSize: [
    { value: '', label: 'Auto' },
    { value: 12, label: '12px' },
    { value: 14, label: '14px' },
    { value: 16, label: '16px' },
    { value: 18, label: '18px' },
    { value: 20, label: '20px' },
    { value: 24, label: '24px' },
    { value: 28, label: '28px' },
    { value: 32, label: '32px' },
  ],
  badgeRadius: [
    { value: '', label: 'Auto' },
    { value: 0, label: 'None' },
    { value: 2, label: '2px' },
    { value: 4, label: '4px' },
    { value: 8, label: '8px' },
    { value: 12, label: '12px' },
    { value: 16, label: '16px' },
    { value: 24, label: '24px' },
    { value: 32, label: '32px' },
    { value: 40, label: '40px' },
    { value: 48, label: '48px' },
    { value: 64, label: '64px' },
  ],
  colorMode: [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'system', label: 'System' },
  ],
  headingSize: [
    { value: 'xs', label: 'Extra small' },
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra large' },
  ],
  bodySize: [
    { value: 'xs', label: 'Extra small' },
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra large' },
  ],
} as const;

export type OptionsRef = keyof typeof THEME_OPTIONS;

// ============================================================================
// Internal helpers for entry generation
// ============================================================================

interface FontDefaults {
  fontFamily?: string;
  fontStyle?: string;
  fontWeight?: string;
  fontSize?: string;
  lineHeight?: string;
}

const FONT_BODY: FontDefaults = {
  fontFamily: 'var(--cp_font-body)',
  fontStyle: 'var(--cp_font-body-style)',
  fontWeight: 'var(--cp_font-body-weight)',
  fontSize: 'var(--cp_font-body-size)',
  lineHeight: 'var(--cp_font-body-line-height)',
};

function fontGroup(
  basePath: string,
  cssVarPrefix: string,
  elemLabel: string,
  cat: ThemePropertyCategory,
  defaults?: FontDefaults,
): ThemeProperty[] {
  const d = defaults;
  return [
    {
      path: `${basePath}.fontFamily`,
      cssProperty: 'fontFamily',
      type: 'font',
      label: 'Font',
      description: `Font family for the ${elemLabel} in the ${cat} section.`,
      category: cat,
      cssVars: [
        {
          cssVar: `${cssVarPrefix}-font-family`,
          transform: 'direct',
          ...(d?.fontFamily && { cssDefault: d.fontFamily }),
        },
      ],
      resetable: true,
    },
    {
      path: `${basePath}.fontStyle`,
      cssProperty: 'fontStyle',
      type: 'fontStyle',
      label: 'Style',
      description: `Font style for the ${elemLabel} in the ${cat} section.`,
      category: cat,
      cssVars: [
        {
          cssVar: `${cssVarPrefix}-font-style`,
          transform: 'direct',
          ...(d?.fontStyle && { cssDefault: d.fontStyle }),
        },
      ],
      options: 'fontStyle' as OptionsRef,
    },
    {
      path: `${basePath}.fontWeight`,
      cssProperty: 'fontWeight',
      type: 'fontWeight',
      label: 'Weight',
      description: `Font weight for the ${elemLabel} in the ${cat} section.`,
      category: cat,
      cssVars: [
        {
          cssVar: `${cssVarPrefix}-font-weight`,
          transform: 'direct',
          ...(d?.fontWeight && { cssDefault: d.fontWeight }),
        },
      ],
      fontFamilyField: `${basePath}.fontFamily`,
    },
    {
      path: `${basePath}.fontSize`,
      cssProperty: 'fontSize',
      type: 'fontSize',
      label: 'Size',
      description: `Font size for the ${elemLabel} in the ${cat} section.`,
      category: cat,
      cssVars: [
        {
          cssVar: `${cssVarPrefix}-font-size`,
          transform: 'px',
          ...(d?.fontSize && { cssDefault: d.fontSize }),
        },
        {
          cssVar: `${cssVarPrefix}-line-height`,
          transform: 'lineHeight',
          ...(d?.lineHeight && { cssDefault: d.lineHeight }),
        },
      ],
      options: 'fontSize' as OptionsRef,
    },
  ];
}

// Full styling parity for a "PWYW input" element: mirrors every Field > Input
// styling control (resting styles, shadow width, focus, placeholder, typography)
// so the amount box can be themed independently. Every default points at the
// matching field-input var (or, via `chainTo`, the base PWYW input's vars), so
// the input looks identical until overridden.
// `prefix` is the cssVar stem, e.g. 'product-price-pwywInput'.
function pwywInputGroup(
  basePath: string,
  prefix: string,
  opts: {
    category?: ThemePropertyCategory;
    defaults?: { paddingY?: string; fontSize?: string; lineHeight?: string };
    chainTo?: string;
  } = {},
): ThemeProperty[] {
  const FI = `var(--cp_${opts.chainTo ?? 'checkout-form-fields-input'}`;
  const cat = opts.category ?? 'product';
  const colorEntry = (
    stylesKey: 'styles' | 'focusStyles' | 'placeholderStyles',
    cssProperty: 'color' | 'backgroundColor' | 'borderColor',
    subPath: 'color' | 'backgroundColor' | 'borderColor',
    varSuffix: string,
    label: string,
    cssDefault?: string,
  ): ThemeProperty => ({
    path: `${basePath}.${stylesKey}.${subPath}`,
    cssProperty,
    type: 'color',
    label,
    description: `${label} of the pay-what-you-want amount input.`,
    category: cat,
    cssVars: [
      {
        cssVar: `${prefix}-${varSuffix}`,
        transform: 'direct',
        ...(cssDefault && { cssDefault }),
      },
    ],
    resetable: true,
  });
  const sizeEntry = (
    stylesKey: 'styles' | 'focusStyles',
    cssProperty: 'borderWidth' | 'borderRadius' | 'paddingX' | 'paddingY',
    subPath: 'borderWidth' | 'borderRadius' | 'paddingX' | 'paddingY',
    varSuffix: string,
    label: string,
    cssDefault: string | undefined,
    options: OptionsRef,
  ): ThemeProperty => ({
    path: `${basePath}.${stylesKey}.${subPath}`,
    cssProperty,
    type: 'size',
    label,
    description: `${label} of the pay-what-you-want amount input.`,
    category: cat,
    cssVars: [
      { cssVar: `${prefix}-${varSuffix}`, transform: 'px', ...(cssDefault && { cssDefault }) },
    ],
    options,
  });

  const focusDefault = (
    restSuffix: 'color' | 'background-color' | 'border-color',
  ): string | undefined =>
    opts.chainTo
      ? `var(--cp_${opts.chainTo}-focus-${restSuffix}, var(--cp_${prefix}-${restSuffix}-set, var(--cp_${opts.chainTo}-${restSuffix}-set, var(--cp_checkout-form-fields-input-focus-${restSuffix}))))`
      : undefined;
  return [
    // --- Resting styles (inherit Field > Input) ---
    // Each color carries a `-set` mirror so the focus chains (see focusDefault
    // and the single-price rules in checkout.css) can tell an explicit resting
    // value apart from the generated default.
    withSetMirrors([
      colorEntry(
        'styles',
        'backgroundColor',
        'backgroundColor',
        'background-color',
        'Background',
        `${FI}-background-color)`,
      ),
    ])[0],
    withSetMirrors([colorEntry('styles', 'color', 'color', 'color', 'Text', `${FI}-color)`)])[0],
    withSetMirrors([
      colorEntry(
        'styles',
        'borderColor',
        'borderColor',
        'border-color',
        'Border',
        `${FI}-border-color)`,
      ),
    ])[0],
    sizeEntry(
      'styles',
      'borderWidth',
      'borderWidth',
      'border-width',
      'Border width',
      `${FI}-border-width)`,
      'borderWidth' as OptionsRef,
    ),
    sizeEntry(
      'styles',
      'borderRadius',
      'borderRadius',
      'border-radius',
      'Border radius',
      `${FI}-border-radius)`,
      'sizePx' as OptionsRef,
    ),
    sizeEntry(
      'styles',
      'paddingX',
      'paddingX',
      'padding-x',
      'Horizontal padding',
      `${FI}-padding-x)`,
      'paddingPx' as OptionsRef,
    ),
    sizeEntry(
      'styles',
      'paddingY',
      'paddingY',
      'padding-y',
      'Vertical padding',
      opts.defaults?.paddingY ?? `${FI}-padding-y)`,
      'paddingPx' as OptionsRef,
    ),
    {
      path: `${basePath}.styles.boxShadow`,
      cssProperty: 'boxShadow',
      type: 'shadow',
      label: 'Shadow width',
      description: 'Shadow size of the pay-what-you-want amount input.',
      category: cat,
      cssVars: [
        {
          cssVar: `${prefix}-box-shadow`,
          transform: 'shadow',
          cssDefault: opts.chainTo
            ? `${FI}-box-shadow)`
            : `${FI}-box-shadow, 0 1px 2px 0 rgb(0 0 0 / 5%))`,
        },
      ],
      options: 'shadow' as OptionsRef,
    },
    ...fontGroup(`${basePath}.styles`, prefix, 'PWYW input', cat, {
      fontFamily: `${FI}-font-family)`,
      fontStyle: `${FI}-font-style)`,
      fontWeight: `${FI}-font-weight)`,
      fontSize: opts.defaults?.fontSize ?? `${FI}-font-size)`,
      lineHeight: opts.defaults?.lineHeight ?? `${FI}-line-height)`,
    }),
    // --- Focus styles (see focusDefault above for the fallback topology) ---
    colorEntry('focusStyles', 'color', 'color', 'focus-color', 'Focus text', focusDefault('color')),
    colorEntry(
      'focusStyles',
      'backgroundColor',
      'backgroundColor',
      'focus-background-color',
      'Focus background',
      focusDefault('background-color'),
    ),
    colorEntry(
      'focusStyles',
      'borderColor',
      'borderColor',
      'focus-border-color',
      'Focus border',
      focusDefault('border-color'),
    ),
    // --- Placeholder ---
    colorEntry(
      'placeholderStyles',
      'color',
      'color',
      'placeholder-color',
      'Placeholder',
      `${FI}-placeholder-color)`,
    ),
  ];
}

function pwywLabelGroup(
  basePath: string,
  prefix: string,
  opts: {
    category?: ThemePropertyCategory;
    chainTo?: string;
    setTiers?: string[];
    setTiersOverride?: Partial<
      Record<
        | 'color'
        | 'paddingBottom'
        | 'fontFamily'
        | 'fontStyle'
        | 'fontWeight'
        | 'fontSize'
        | 'lineHeight',
        string[]
      >
    >;
    fallback?: {
      color: string;
      paddingBottom: string;
      fontFamily: string;
      fontStyle: string;
      fontWeight: string;
      fontSize: string;
      lineHeight: string;
    };
  } = {},
): ThemeProperty[] {
  const cat = opts.category ?? 'pwyw';
  const chainTo = opts.chainTo;
  const restDefault = (
    varSuffix: string,
    fallback?: string,
    tiers: string[] = opts.setTiers ?? [],
  ): string | undefined => {
    if (!chainTo) return undefined;
    let inner = fallback;
    for (let i = tiers.length - 1; i >= 0; i--) {
      inner = `var(--cp_${tiers[i]}-${varSuffix}-set, ${inner})`;
    }
    return `var(--cp_${chainTo}-${varSuffix}, ${inner})`;
  };
  const colorDefault = restDefault('color', opts.fallback?.color, opts.setTiersOverride?.color);
  const paddingBottomDefault = restDefault(
    'padding-bottom',
    opts.fallback?.paddingBottom,
    opts.setTiersOverride?.paddingBottom,
  );
  const fontFamilyDefault = restDefault(
    'font-family',
    opts.fallback?.fontFamily,
    opts.setTiersOverride?.fontFamily,
  );
  const fontStyleDefault = restDefault(
    'font-style',
    opts.fallback?.fontStyle,
    opts.setTiersOverride?.fontStyle,
  );
  const fontWeightDefault = restDefault(
    'font-weight',
    opts.fallback?.fontWeight,
    opts.setTiersOverride?.fontWeight,
  );
  const fontSizeDefault = restDefault(
    'font-size',
    opts.fallback?.fontSize,
    opts.setTiersOverride?.fontSize,
  );
  const lineHeightDefault = restDefault(
    'line-height',
    opts.fallback?.lineHeight,
    opts.setTiersOverride?.lineHeight,
  );
  // Focus default: chained groups resolve base focus set → own explicit
  // resting (`-set` mirror, anti-flash) → base explicit resting (base label
  // vars are set-only, no mirror needed) → Fields > Label focus WHEN SET
  // (`-set` mirror — its primary var defaults to Fields resting, which must
  // not beat the Cards-derived fallback) → own resolved resting chain
  // (mirroring pwywInputGroup's `focusDefault` topology). The base group has
  // no cssDefault at all, matching its resting vars; the single-price
  // heading's focus chain lives in checkout.css instead.
  const focusColorDefault = chainTo
    ? `var(--cp_${chainTo}-focus-color, var(--cp_${prefix}-color-set, var(--cp_${chainTo}-color, var(--cp_checkout-form-fields-label-focus-color-set, var(--cp_${prefix}-color)))))`
    : undefined;
  return [
    {
      path: `${basePath}.styles.color`,
      cssProperty: 'color',
      type: 'color',
      label: 'Text',
      description: 'Color of the pay-what-you-want label.',
      category: cat,
      cssVars: [
        {
          cssVar: `${prefix}-color`,
          transform: 'direct',
          ...(colorDefault && { cssDefault: colorDefault }),
        },
        // Chained groups mirror the resting color so their focus chain can
        // tell an explicit resting override apart from the generated default
        // (see focusColorDefault). The base group's vars are already set-only.
        ...(chainTo ? [{ cssVar: `${prefix}-color-set`, transform: 'direct' as const }] : []),
      ],
      resetable: true,
    },
    {
      path: `${basePath}.styles.paddingBottom`,
      cssProperty: 'paddingBottom',
      type: 'size',
      label: 'Padding bottom',
      description: 'Bottom padding below the pay-what-you-want label.',
      category: cat,
      cssVars: [
        {
          cssVar: `${prefix}-padding-bottom`,
          transform: 'px',
          ...(paddingBottomDefault && { cssDefault: paddingBottomDefault }),
        },
      ],
      options: 'paddingPx' as OptionsRef,
    },
    ...fontGroup(`${basePath}.styles`, prefix, 'pay-what-you-want label', cat, {
      fontFamily: fontFamilyDefault,
      fontStyle: fontStyleDefault,
      fontWeight: fontWeightDefault,
      fontSize: fontSizeDefault,
      lineHeight: lineHeightDefault,
    }),
    {
      path: `${basePath}.focusStyles.color`,
      cssProperty: 'color',
      type: 'color',
      label: 'Focus color',
      description: 'Color of the pay-what-you-want label when the input is focused.',
      category: cat,
      cssVars: [
        {
          cssVar: `${prefix}-focus-color`,
          transform: 'direct',
          ...(focusColorDefault && { cssDefault: focusColorDefault }),
        },
      ],
      resetable: true,
    },
  ];
}

function withSetMirrors(props: ThemeProperty[]): ThemeProperty[] {
  return props.map(p => ({
    ...p,
    cssVars: [
      ...p.cssVars,
      ...p.cssVars.map(m => ({ cssVar: `${m.cssVar}-set`, transform: m.transform })),
    ],
  }));
}

function tog(path: string, elemLabel: string, cat: ThemePropertyCategory): ThemeProperty {
  return {
    path,
    type: 'toggle',
    label: `Show ${elemLabel}`,
    description: `Whether the ${elemLabel} is visible in the ${cat} section.`,
    category: cat,
    cssVars: [],
    defaultValue: true,
  };
}

function optionDetailLine(
  elem: string,
  cssVarPrefix: string,
  label: string,
  elemLabel: string,
): ThemeProperty[] {
  const basePath = `variables.elements.product.pricePicker.option.${elem}.styles`;
  return [
    {
      path: `${basePath}.color`,
      cssProperty: 'color',
      type: 'color',
      label,
      description: `Color of the ${elemLabel} line in each price option.`,
      category: 'product',
      cssVars: [
        {
          cssVar: `${cssVarPrefix}-color`,
          transform: 'direct',
          cssDefault:
            'var(--cp_price-picker-option-description-color-set, var(--cp_price-picker-option-color-set, var(--cp_text-secondary)))',
        },
      ],
      resetable: true,
    },
    ...fontGroup(basePath, cssVarPrefix, `${elemLabel} line`, 'product', {
      fontFamily:
        'var(--cp_price-picker-option-description-font-family-set, var(--cp_price-picker-option-font-family-set, var(--cp_font-body)))',
      fontStyle:
        'var(--cp_price-picker-option-description-font-style-set, var(--cp_price-picker-option-font-style-set, var(--cp_font-body-style)))',
      fontWeight:
        'var(--cp_price-picker-option-description-font-weight-set, var(--cp_price-picker-option-font-weight-set, var(--cp_font-body-weight)))',
      fontSize:
        'var(--cp_price-picker-option-description-font-size-set, var(--cp_price-picker-option-font-size-set, var(--cp_font-body-size)))',
      lineHeight:
        'var(--cp_price-picker-option-description-line-height-set, var(--cp_price-picker-option-line-height-set, inherit))',
    }),
  ];
}

// ============================================================================
// Token entries
// ============================================================================

const TOKEN_ENTRIES: ThemeProperty[] = [
  {
    path: 'variables.tokens.colorPrimary',
    type: 'color',
    label: 'Main color',
    description:
      'Primary brand color used for buttons, links, and accents. Produces primary, primary-dark, logo-text, and logo-bg CSS variables.',
    category: 'tokens',
    cssVars: [
      { cssVar: 'color-primary', transform: 'direct' },
      { cssVar: 'color-primary-dark', transform: 'direct', note: 'darken 10%' },
      { cssVar: 'store-logo-text-color', transform: 'direct', note: 'safe readable contrast' },
      { cssVar: 'store-logo-background-color', transform: 'direct' },
    ],
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.tokens.colorSecondary',
    type: 'color',
    label: 'Accent color',
    description: 'Secondary accent color. Produces secondary and secondary-dark CSS variables.',
    category: 'tokens',
    cssVars: [
      { cssVar: 'color-secondary', transform: 'direct' },
      { cssVar: 'color-secondary-dark', transform: 'direct', note: 'darken 10%' },
    ],
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.tokens.textPrimary',
    type: 'color',
    label: 'Main text color',
    description: 'Primary text color used throughout the checkout.',
    category: 'tokens',
    cssVars: [{ cssVar: 'text-primary', transform: 'direct' }],
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.tokens.textSecondary',
    type: 'color',
    label: 'Secondary text color',
    description: 'Secondary text color for less prominent text elements.',
    category: 'tokens',
    cssVars: [{ cssVar: 'text-secondary', transform: 'direct' }],
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.tokens.textTertiary',
    type: 'color',
    label: 'Supporting text color',
    description:
      'Supporting text color for auxiliary elements like "Pay with card" and "Payments are secure & encrypted".',
    category: 'tokens',
    cssVars: [{ cssVar: 'text-tertiary', transform: 'direct' }],
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.tokens.colorBackground',
    type: 'color',
    label: 'Background color',
    description: 'Decorative page chrome color. No text sits directly on this color.',
    category: 'tokens',
    cssVars: [],
    derived: true,
    resetable: true,
    uiMeta: { picker: 'hex' },
  },
  {
    path: 'variables.tokens.colorSurface',
    type: 'color',
    label: 'Main column color',
    description: 'Main column surface color for forms and cards.',
    category: 'tokens',
    cssVars: [],
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.tokens.colorSurfaceAccent',
    type: 'color',
    label: 'Content column',
    description:
      'Content column surface color — text sits on it. Used for the double layout content panel by default.',
    category: 'tokens',
    cssVars: [],
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.tokens.radius',
    type: 'select',
    label: 'Border radius',
    description:
      'Global border radius token. Produces 4 CSS variables for checkout, card, input, and button border-radius.',
    category: 'tokens',
    cssVars: [
      { cssVar: 'checkout-border-radius', transform: 'direct' },
      { cssVar: 'card-border-radius', transform: 'direct' },
      { cssVar: 'input-border-radius', transform: 'direct' },
      { cssVar: 'button-border-radius', transform: 'direct' },
    ],
    options: 'radius' as OptionsRef,
    defaultValue: 'sm',
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.tokens.colors',
    type: 'select',
    label: 'Color mode',
    description: 'Color mode for the checkout: light, dark, or system preference.',
    category: 'tokens',
    cssVars: [],
    options: 'colorMode' as OptionsRef,
    defaultValue: 'light',
    resetable: true,
  },
  {
    path: 'variables.locale',
    type: 'select',
    label: 'Currency format',
    description: 'Locale used for currency and number formatting.',
    category: 'tokens',
    cssVars: [],
    defaultValue: 'en-US',
    resetable: true,
  },
  {
    path: 'variables.tokens.fontMain.family',
    type: 'font',
    label: 'Main font',
    description:
      'Base font family inherited by both Title and Text fonts when they are set to Auto.',
    category: 'tokens',
    cssVars: [],
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.tokens.fontHeading.family',
    type: 'font',
    label: 'Title font',
    description: 'Font family for all heading text. Applied with fallback chain.',
    category: 'tokens',
    cssVars: [{ cssVar: 'font-heading', transform: 'direct' }],
    derived: true,
    resetable: true,
    parentFontField: 'variables.tokens.fontMain.family',
  },
  {
    path: 'variables.tokens.fontHeading.size',
    type: 'select',
    label: 'Title sizes',
    description: 'Heading size preset. Produces h1-h6 font-size and line-height CSS variables.',
    category: 'tokens',
    cssVars: [
      { cssVar: 'font-heading-size-h1', transform: 'direct' },
      { cssVar: 'font-heading-line-height-h1', transform: 'direct' },
      { cssVar: 'font-heading-size-h2', transform: 'direct' },
      { cssVar: 'font-heading-line-height-h2', transform: 'direct' },
      { cssVar: 'font-heading-size-h3', transform: 'direct' },
      { cssVar: 'font-heading-line-height-h3', transform: 'direct' },
      { cssVar: 'font-heading-size-h4', transform: 'direct' },
      { cssVar: 'font-heading-line-height-h4', transform: 'direct' },
      { cssVar: 'font-heading-size-h5', transform: 'direct' },
      { cssVar: 'font-heading-line-height-h5', transform: 'direct' },
      { cssVar: 'font-heading-size-h6', transform: 'direct' },
      { cssVar: 'font-heading-line-height-h6', transform: 'direct' },
    ],
    options: 'headingSize' as OptionsRef,
    defaultValue: 'md',
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.tokens.fontHeading.weight',
    type: 'fontWeight',
    label: 'Title weight',
    description: 'Font weight for all headings.',
    category: 'tokens',
    cssVars: [{ cssVar: 'font-heading-weight', transform: 'direct' }],
    fontFamilyField: 'variables.tokens.fontHeading.family',
    defaultValue: 700,
    derived: true,
    uiMeta: { showAutoOption: false },
  },
  {
    path: 'variables.tokens.fontBody.family',
    type: 'font',
    label: 'Text font',
    description: 'Font family for body text. Applied with fallback chain.',
    category: 'tokens',
    cssVars: [{ cssVar: 'font-body', transform: 'direct' }],
    derived: true,
    resetable: true,
    parentFontField: 'variables.tokens.fontMain.family',
  },
  {
    path: 'variables.tokens.fontBody.size',
    type: 'select',
    label: 'Text size',
    description:
      'Body text size preset. Produces xs/sm/base/lg font-size and line-height CSS variables.',
    category: 'tokens',
    cssVars: [
      { cssVar: 'font-body-size-xs', transform: 'direct' },
      { cssVar: 'font-body-line-height-xs', transform: 'direct' },
      { cssVar: 'font-body-size-sm', transform: 'direct' },
      { cssVar: 'font-body-line-height-sm', transform: 'direct' },
      { cssVar: 'font-body-size', transform: 'direct' },
      { cssVar: 'font-body-line-height', transform: 'direct' },
      { cssVar: 'font-body-size-lg', transform: 'direct' },
      { cssVar: 'font-body-line-height-lg', transform: 'direct' },
    ],
    options: 'bodySize' as OptionsRef,
    defaultValue: 'md',
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.tokens.fontBody.weight',
    type: 'fontWeight',
    label: 'Text weight',
    description: 'Font weight for body text. Also derives label weight (+100).',
    category: 'tokens',
    cssVars: [
      { cssVar: 'font-body-weight', transform: 'direct' },
      { cssVar: 'font-label-weight', transform: 'direct', note: 'body weight + 100' },
    ],
    fontFamilyField: 'variables.tokens.fontBody.family',
    defaultValue: 400,
    derived: true,
    uiMeta: { showAutoOption: false },
  },
];

// ============================================================================
// Layout entries
// ============================================================================

const LAYOUT_ENTRIES: ThemeProperty[] = [
  // Per-layout color overrides (resolution happens in JS, not via cssVars)
  // Center layout
  {
    path: 'variables.layout.centerLayout.backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Page background color override for center layout.',
    category: 'layout',
    cssVars: [],
    resetable: true,
    uiMeta: { picker: 'hex' },
  },
  {
    path: 'variables.layout.centerLayout.contentBackgroundColor',
    type: 'color',
    label: 'Content background',
    description: 'Content background color override for center layout.',
    category: 'layout',
    cssVars: [],
    resetable: true,
  },
  {
    path: 'variables.layout.centerLayout.formBackgroundColor',
    type: 'color',
    label: 'Form background',
    description: 'Form background color override for center layout.',
    category: 'layout',
    cssVars: [],
    resetable: true,
  },
  {
    path: 'variables.layout.centerLayout.backgroundImage',
    type: 'image',
    label: 'Background image',
    description: 'Background image for center layout.',
    category: 'layout',
    cssVars: [],
    resetable: true,
    uiMeta: { previewSize: 'background' },
  },
  {
    path: 'variables.layout.centerLayout.backgroundPosition',
    type: 'select',
    label: 'Background position',
    description: 'Background image position for center layout.',
    category: 'layout',
    cssVars: [],
    options: [
      { value: 'center', label: 'Center' },
      { value: 'top', label: 'Top' },
      { value: 'bottom', label: 'Bottom' },
      { value: 'left', label: 'Left' },
      { value: 'right', label: 'Right' },
    ],
    defaultValue: 'center',
    resetable: true,
  },
  // Double layout
  {
    path: 'variables.layout.doubleLayout.contentBackgroundColor',
    type: 'color',
    label: 'Content background',
    description: 'Content background color override for double layout.',
    category: 'layout',
    cssVars: [],
    resetable: true,
    uiMeta: { picker: 'hex' },
  },
  {
    path: 'variables.layout.doubleLayout.formBackgroundColor',
    type: 'color',
    label: 'Form background',
    description: 'Form background color override for double layout.',
    category: 'layout',
    cssVars: [],
    resetable: true,
    uiMeta: { picker: 'hex' },
  },
  // Split layout
  {
    path: 'variables.layout.splitLayout.backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Page background color override for split layout.',
    category: 'layout',
    cssVars: [],
    resetable: true,
    uiMeta: { picker: 'hex' },
  },
  {
    path: 'variables.layout.splitLayout.contentBackgroundColor',
    type: 'color',
    label: 'Content background',
    description: 'Content background color override for split layout.',
    category: 'layout',
    cssVars: [],
    resetable: true,
  },
  {
    path: 'variables.layout.splitLayout.formBackgroundColor',
    type: 'color',
    label: 'Form background',
    description: 'Form background color override for split layout.',
    category: 'layout',
    cssVars: [],
    resetable: true,
  },
  {
    path: 'variables.layout.splitLayout.backgroundImage',
    type: 'image',
    label: 'Background image',
    description: 'Background image for split layout.',
    category: 'layout',
    cssVars: [],
    resetable: true,
    uiMeta: { previewSize: 'background' },
  },
  {
    path: 'variables.layout.splitLayout.backgroundPosition',
    type: 'select',
    label: 'Background position',
    description: 'Background image position for split layout.',
    category: 'layout',
    cssVars: [],
    options: [
      { value: 'center', label: 'Center' },
      { value: 'top', label: 'Top' },
      { value: 'bottom', label: 'Bottom' },
      { value: 'left', label: 'Left' },
      { value: 'right', label: 'Right' },
    ],
    defaultValue: 'center',
    resetable: true,
  },
  {
    path: 'variables.layout.shadowColor',
    type: 'color',
    label: 'Shadow color',
    description: 'Color of the checkout container shadow.',
    category: 'layout',
    cssVars: [{ cssVar: 'checkout-shadow-color', transform: 'direct', cssDefault: '#d4d4d4' }],
    derived: true,
    resetable: true,
  },
  {
    path: 'variables.layout.shadowWidth',
    type: 'shadow',
    label: 'Shadow width',
    description: 'Size of the checkout container shadow.',
    category: 'layout',
    cssVars: [
      {
        cssVar: 'checkout-shadow',
        transform: 'shadow',
        cssDefault:
          '0 1px 3px 0 var(--cp_checkout-shadow-color), 0 1px 2px -1px var(--cp_checkout-shadow-color)',
      },
      {
        cssVar: 'checkout-form-background-shadow-right',
        transform: 'direct',
        note: 'side shadow via getSideShadowStyle',
        cssDefault: '2px 0 6px -1px var(--cp_checkout-shadow-color)',
      },
      {
        cssVar: 'checkout-form-background-shadow-left',
        transform: 'direct',
        note: 'side shadow via getSideShadowStyle',
        cssDefault: '-2px 0 6px -1px var(--cp_checkout-shadow-color)',
      },
    ],
    options: 'shadow' as OptionsRef,
    derived: true,
  },
  {
    path: 'variables.layout.doubleLayout.formPosition',
    type: 'select',
    label: 'Form position',
    description: 'Position of the form in double column layout.',
    category: 'layout',
    cssVars: [],
    options: [
      { value: 'right', label: 'Content left \u00b7 Form right' },
      { value: 'left', label: 'Form left \u00b7 Content right' },
    ],
    defaultValue: 'right',
    resetable: true,
  },
  {
    path: 'variables.layout.doubleLayout.backgroundWidth',
    type: 'select',
    label: 'Content background width',
    description: 'Content background width in double column layout.',
    category: 'layout',
    cssVars: [],
    options: [
      { value: 'half', label: 'Half' },
      { value: 'full', label: 'Full' },
    ],
    defaultValue: 'half',
    resetable: true,
  },
  {
    path: 'variables.layout.splitLayout.mainColumn',
    type: 'select',
    label: 'Main column',
    description: 'Which side the main column appears on in split layout.',
    category: 'layout',
    cssVars: [],
    options: [
      { value: 'left', label: 'Left' },
      { value: 'right', label: 'Right' },
    ],
    defaultValue: 'left',
    resetable: true,
  },
];

// ============================================================================
// Product entries (container, product, logo, pricing, events)
// ============================================================================

const PRODUCT_ENTRIES: ThemeProperty[] = [
  // --- Container ---
  {
    path: 'variables.elements.container.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Container background',
    description: 'Background color of the outer container.',
    category: 'checkout',
    cssVars: [{ cssVar: 'container-background-color', transform: 'direct' }],
  },
  {
    path: 'variables.elements.container.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Container text color',
    description: 'Text color of the outer container.',
    category: 'checkout',
    cssVars: [{ cssVar: 'container-color', transform: 'direct' }],
  },
  // --- Product ---
  {
    path: 'variables.elements.product.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Product background',
    description: 'Background color of the product section.',
    category: 'product',
    cssVars: [{ cssVar: 'product-background-color', transform: 'direct' }],
  },
  {
    path: 'variables.elements.product.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Product text color',
    description: 'Default text color of the product section.',
    category: 'product',
    cssVars: [
      { cssVar: 'product-color', transform: 'direct', cssDefault: 'var(--cp_text-primary)' },
    ],
  },
  {
    path: 'variables.elements.product.styles.textAlign',
    cssProperty: 'textAlign',
    type: 'alignment',
    label: 'Text alignment',
    description: 'Text alignment for the product section.',
    category: 'product',
    cssVars: [{ cssVar: 'product-text-align', transform: 'direct', cssDefault: "'left'" }],
    options: [
      { value: 'left', label: 'Left' },
      { value: 'center', label: 'Center' },
      { value: 'right', label: 'Right' },
    ],
    derived: true,
  },
  // --- Product title ---
  {
    path: 'variables.elements.product.title.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Title',
    description: 'Color of the product title text.',
    category: 'product',
    cssVars: [
      { cssVar: 'product-title-color', transform: 'direct', cssDefault: 'var(--cp_text-primary)' },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.title.styles',
    'product-title',
    'product title',
    'product',
    {
      fontFamily: 'var(--cp_font-heading)',
      fontStyle: 'var(--cp_font-heading-style)',
      fontWeight: 'var(--cp_font-heading-weight)',
      fontSize: 'var(--cp_font-heading-size-h2)',
      lineHeight: 'var(--cp_font-heading-line-height-h2)',
    },
  ),
  // --- Product pricing - price (the amount) — single-price block only ---
  {
    path: 'variables.elements.product.pricing.price.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Price',
    description: 'Color of the product price.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricing.price.styles',
    'product-price',
    'product price',
    'product',
    {
      ...FONT_BODY,
      fontSize: 'var(--cp_font-price-size)',
      lineHeight: 'var(--cp_font-price-line-height)',
    },
  ),
  // --- Product pricing - interval (the "/ month") — single-price block AND the
  // --- multi-price picker option lines share this element via the
  // --- cp_product-price-interval class. All vars default to `inherit` so the
  // --- interval matches its surrounding price text until a seller overrides it.
  {
    path: 'variables.elements.product.pricing.interval.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Interval',
    description: 'Color of the subscription interval (e.g. "/ month").',
    category: 'product',
    cssVars: [
      { cssVar: 'product-price-interval-color', transform: 'direct', cssDefault: 'inherit' },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricing.interval.styles',
    'product-price-interval',
    'product price interval',
    'product',
    {
      fontFamily: 'inherit',
      fontStyle: 'inherit',
      fontWeight: 'inherit',
      fontSize: 'inherit',
      lineHeight: 'inherit',
    },
  ),
  // --- Product pricing - label removed: the single pay-what-you-want heading's
  // --- Product pricing - discountedFromPrice — single-price block only ---
  {
    path: 'variables.elements.product.pricing.discountedFromPrice.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Discounted from',
    description: 'Color of the original price shown with a discount.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-discountedFromPrice-color',
        transform: 'direct',
        // Follows the single-price Price element, not Cards (matches the design
        // editor's resolver, which already treats discounted-from as ← Price).
        cssDefault: 'var(--cp_product-price-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricing.discountedFromPrice.styles',
    'product-price-discountedFromPrice',
    'discounted from price',
    'product',
    {
      // Font follows the single-price Price element, not Cards.
      fontFamily: 'var(--cp_product-price-font-family)',
      fontStyle: 'var(--cp_product-price-font-style)',
      fontWeight: 'var(--cp_product-price-font-weight)',
      fontSize: 'var(--cp_font-price-size)',
      lineHeight: 'var(--cp_font-price-line-height)',
    },
  ),
  // --- Product pricing - trialPeriod — single-price block only ---
  {
    path: 'variables.elements.product.pricing.trialPeriod.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Trial period',
    description: 'Color of the trial period text.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-trialPeriod-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricing.trialPeriod.styles',
    'product-price-trialPeriod',
    'trial period',
    'product',
    {
      ...FONT_BODY,
      fontSize: 'var(--cp_font-price-size)',
      lineHeight: 'var(--cp_font-price-line-height)',
    },
  ),
  // --- Product pricing - startDate — single-price block only ---
  {
    path: 'variables.elements.product.pricing.startDate.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Start date',
    description: 'Color of the subscription start date text.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-startDate-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricing.startDate.styles',
    'product-price-startDate',
    'start date',
    'product',
    FONT_BODY,
  ),
  // --- Product pricing - planIterations — single-price block only ---
  {
    path: 'variables.elements.product.pricing.planIterations.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Number of payments',
    description: 'Color of the number of payments text.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-planIterations-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricing.planIterations.styles',
    'product-price-planIterations',
    'plan iterations',
    'product',
    FONT_BODY,
  ),
  // --- Product pricing - prorationRenews — single-price block only ---
  {
    path: 'variables.elements.product.pricing.prorationRenews.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Proration renews',
    description: 'Color of the proration renewal date text.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-prorationRenews-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricing.prorationRenews.styles',
    'product-price-prorationRenews',
    'proration renews',
    'product',
    FONT_BODY,
  ),
  // --- Product pricing - prorationAmount ---
  {
    path: 'variables.elements.product.pricing.prorationAmount.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Proration amount',
    description: 'Color of the proration amount text.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-prorationAmount-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricing.prorationAmount.styles',
    'product-price-prorationAmount',
    'proration amount',
    'product',
    FONT_BODY,
  ),
  // --- Product pricing - setupFee — single-price block only ---
  {
    path: 'variables.elements.product.pricing.setupFee.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Setup fee',
    description: 'Color of the setup fee text.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-setupFee-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricing.setupFee.styles',
    'product-price-setupFee',
    'setup fee',
    'product',
    FONT_BODY,
  ),
  {
    path: 'variables.elements.product.pricing.badge.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Highlight text',
    description: 'Text color of the single-price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-badge-color',
        transform: 'direct',
        cssDefault: 'var(--color-white)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Highlight background',
    description: 'Background color of the single-price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-badge-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_color-secondary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Highlight border',
    description: 'Border color of the single-price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-badge-border-color',
        transform: 'direct',
        // Defaults to the highlight background so the border is invisible until
        // a seller picks a contrasting colour.
        cssDefault: 'var(--cp_product-price-badge-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Highlight border width',
    description: 'Border width of the single-price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-badge-border-width',
        transform: 'px',
        cssDefault: '0px',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Highlight border radius',
    description: 'Border radius of the single-price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-badge-border-radius',
        transform: 'px',
        // Follows the global Border radius token via the page-level var, NOT the
        // Cards element radius — so a Cards > Card border-radius override no longer
        // drags the highlight along; overridable per the badgeRadius set.
        cssDefault: 'var(--cp_checkout-border-radius)',
      },
    ],
    options: 'badgeRadius' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricing.badge.styles.padding',
    cssProperty: 'padding',
    type: 'size',
    label: 'Highlight padding',
    description: 'Padding inside the single-price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-price-badge-padding',
        transform: 'px',
        cssDefault: '0.125rem 0.5rem',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  ...fontGroup(
    'variables.elements.product.pricing.badge.styles',
    'product-price-badge',
    'single-price highlight',
    'product',
    {
      fontFamily: 'var(--cp_font-body)',
      fontStyle: 'var(--cp_font-body-style)',
      fontWeight: '600',
      // The small (fine-print) step of the body scale — 12px at the medium global
      // text size, scaling with the global setting like the secure-payments text.
      fontSize: 'var(--cp_font-body-size-sm)',
      lineHeight: '1.4',
    },
  ),
  // --- Product pricing - pwywInput removed: the single-price PWYW amount box is
  // now themed by the base "Pay what you want" element (checkout.pwyw.input),
  // which all three PWYW inputs (single, multi-price option, variant) chain to
  // or inherit from. See PWYW_ENTRIES.
  // --- Logo banner ---
  {
    path: 'variables.elements.product.logo.hostedPageLogoStyle',
    type: 'select',
    label: 'Hosted page logo',
    description: 'Which logo variant to show on hosted pages.',
    category: 'product',
    cssVars: [],
    options: [
      { value: 'logoBanner', label: 'Banner' },
      { value: 'logoBubble', label: 'Bubble' },
    ],
    defaultValue: 'logoBanner',
  },
  {
    path: 'variables.elements.product.logo.logoBanner.bannerStyle',
    type: 'select',
    label: 'Banner style',
    description: 'Banner logo display variant.',
    category: 'product',
    cssVars: [],
    options: [
      { value: 'full', label: 'Mark and name' },
      { value: 'icon', label: 'Mark only' },
      { value: 'name', label: 'Name only' },
    ],
    defaultValue: 'full',
  },
  {
    path: 'variables.elements.product.logo.logoBanner.styles.size',
    cssProperty: 'size',
    type: 'size',
    label: 'Mark size',
    description: 'Size of the logo mark in the banner.',
    category: 'product',
    cssVars: [],
    options: 'logoSizePx' as OptionsRef,
    defaultValue: '',
    resetable: true,
  },
  {
    path: 'variables.elements.product.logo.logoBanner.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Name',
    description: 'Color of the logo name text in the banner.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-logo-banner-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.logo.logoBanner.styles',
    'product-logo-banner',
    'logo banner',
    'product',
    { fontFamily: 'var(--cp_font-heading)', fontSize: '1rem', fontWeight: '500' },
  ),
  // --- Logo bubble ---
  {
    path: 'variables.elements.product.logo.logoBubble.styles.size',
    cssProperty: 'size',
    type: 'size',
    label: 'Size',
    description: 'Size of the logo bubble.',
    category: 'product',
    cssVars: [],
    options: 'logoSizePx' as OptionsRef,
    defaultValue: '',
  },
  {
    path: 'variables.elements.product.logo.logoBubble.styles.textAlign',
    cssProperty: 'textAlign',
    type: 'alignment',
    label: 'Alignment',
    description: 'Alignment of the logo bubble.',
    category: 'product',
    cssVars: [
      { cssVar: 'product-logo-bubble-text-align', transform: 'direct', cssDefault: "'left'" },
    ],
    options: [
      { value: 'left', label: 'Left' },
      { value: 'center', label: 'Center' },
      { value: 'right', label: 'Right' },
    ],
    defaultValue: 'center',
  },
  {
    path: 'variables.elements.product.logo.logoBubble.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color behind the logo bubble image.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-logo-bubble-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_content-background-color)',
      },
    ],
    resetable: true,
  },
  // --- Store logo (CSS-only, no dashboard fields) ---
  {
    path: 'variables.elements.product.store.logo.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Store logo background',
    description: 'Background color of the store logo.',
    category: 'product',
    cssVars: [{ cssVar: 'product-store-logo-background-color', transform: 'direct' }],
  },
  {
    path: 'variables.elements.product.store.logo.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Store logo color',
    description: 'Text color of the store logo.',
    category: 'product',
    cssVars: [{ cssVar: 'product-store-logo-color', transform: 'direct' }],
  },
  {
    path: 'variables.elements.product.store.logo.styles.textAlign',
    cssProperty: 'textAlign',
    type: 'alignment',
    label: 'Store logo alignment',
    description: 'Alignment of the store logo.',
    category: 'product',
    cssVars: [{ cssVar: 'product-store-logo-align', transform: 'direct' }],
  },
  // --- Store logo with store name (CSS-only) ---
  {
    path: 'variables.elements.product.store.logoWithStoreName.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Logo with store name background',
    description: 'Background color of the logo with store name.',
    category: 'product',
    cssVars: [{ cssVar: 'product-store-logoWithStoreName-background-color', transform: 'direct' }],
  },
  {
    path: 'variables.elements.product.store.logoWithStoreName.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Logo with store name color',
    description: 'Text color of the logo with store name.',
    category: 'product',
    cssVars: [{ cssVar: 'product-store-logoWithStoreName-color', transform: 'direct' }],
  },
  // --- Events ---
  {
    path: 'variables.elements.product.event.date.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Event date',
    description: 'Color of the event date text.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-event-date-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.event.location.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Event location',
    description: 'Color of the event location text.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-event-location-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.event.virtual.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Virtual/Hybrid event',
    description: 'Color of the virtual/hybrid event text.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'product-event-virtual-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
];

// ============================================================================
// Description entries
// ============================================================================

const DESCRIPTION_ENTRIES: ThemeProperty[] = [
  {
    path: 'variables.elements.description.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Description background',
    description: 'Background color of the description section.',
    category: 'description',
    cssVars: [{ cssVar: 'description-background-color', transform: 'direct' }],
  },
  {
    path: 'variables.elements.description.headings.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Headings',
    description: 'Color of headings in the description section.',
    category: 'description',
    cssVars: [
      {
        cssVar: 'description-heading-text-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.description.body.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Body text',
    description: 'Color of body text in the description section.',
    category: 'description',
    cssVars: [
      {
        cssVar: 'description-body-text-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.description.links.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Links',
    description: 'Color of links in the description section.',
    category: 'description',
    cssVars: [
      {
        cssVar: 'description-link-text-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.description.quotes.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Quotes',
    description: 'Color of blockquotes in the description section.',
    category: 'description',
    cssVars: [
      {
        cssVar: 'description-quote-text-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.description.bullets.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Bullets',
    description: 'Color of the marker on bulleted lists in the description.',
    category: 'description',
    cssVars: [
      {
        cssVar: 'description-bullet-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.description.bullets.styles.fontSize',
    cssProperty: 'fontSize',
    type: 'size',
    label: 'Bullet size',
    description: 'Size of the marker on bulleted lists in the description.',
    category: 'description',
    cssVars: [
      {
        cssVar: 'description-bullet-size',
        transform: 'px',
        // 1em (the item's own size, which is what an unset marker inherited
        // anyway) rather than `inherit`, so the gutter can be sized off it.
        cssDefault: '1em',
      },
    ],
    // Its own list rather than the shared fontSize one, which runs to 64px —
    // sizes a marker beside body text has no use for.
    options: [
      { value: '', label: 'Auto' },
      { value: 10, label: '10px' },
      { value: 12, label: '12px' },
      { value: 14, label: '14px' },
      { value: 16, label: '16px' },
      { value: 18, label: '18px' },
      { value: 20, label: '20px' },
      { value: 24, label: '24px' },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.description.bullets.align',
    type: 'select',
    label: 'Bullet alignment',
    description: 'Where the marker sits against the first line of a list item.',
    category: 'description',
    options: [
      { value: 'top', label: 'Top' },
      { value: 'middle', label: 'Middle' },
      { value: 'bottom', label: 'Bottom' },
    ],
    cssVars: [
      {
        cssVar: 'description-bullet-align',
        transform: 'direct',
        cssDefault: 'middle',
      },
    ],
    defaultValue: 'middle',
    resetable: true,
  },
  {
    path: 'variables.elements.description.bullets.type',
    type: 'select',
    label: 'Bullet style',
    description: 'Marker shown on bulleted lists in the description.',
    category: 'description',
    options: [
      { value: 'dot', label: 'Dot' },
      { value: 'tick', label: 'Tick' },
      { value: 'dash', label: 'Dash' },
      { value: 'arrow', label: 'Arrow' },
    ],
    cssVars: [
      {
        cssVar: 'description-bullet-type',
        transform: 'direct',
        cssDefault: 'dot',
      },
    ],
    defaultValue: 'dot',
    resetable: true,
  },
  {
    path: 'variables.elements.description.collapsibles.title.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Accordion title',
    description: 'Color of accordion titles in the description.',
    category: 'description',
    cssVars: [
      {
        cssVar: 'description-collapsibles-title-text-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.description.collapsibles.body.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Accordion text',
    description: 'Color of accordion body text in the description.',
    category: 'description',
    cssVars: [
      {
        cssVar: 'description-collapsibles-body-text-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.description.collapsibles.icon.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Accordion icon',
    description: 'Color of the accordion toggle icon in the description.',
    category: 'description',
    cssVars: [
      {
        cssVar: 'description-collapsibles-icon-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.description.collapsibles.icon.type',
    type: 'select',
    label: 'Accordion icon',
    description: 'Icon shown on accordion toggles in the description.',
    category: 'description',
    options: [
      { value: 'chevron', label: 'Chevron' },
      { value: 'plus', label: 'Plus' },
      { value: 'caret', label: 'Caret' },
    ],
    cssVars: [
      {
        cssVar: 'description-collapsibles-icon-type',
        transform: 'direct',
        cssDefault: 'chevron',
      },
    ],
    defaultValue: 'chevron',
    resetable: true,
  },
  {
    path: 'variables.elements.description.collapsibles.border.enabled',
    type: 'toggle',
    label: 'Accordion border',
    description: 'Show/hide the bottom border on accordions in the description.',
    category: 'description',
    cssVars: [],
  },
  {
    path: 'variables.elements.description.collapsibles.border.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Accordion border',
    description: 'Color of the bottom border on accordions.',
    category: 'description',
    cssVars: [
      {
        cssVar: 'description-collapsibles-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_border-default)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.description.collapsibles.border.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Accordion border width',
    description: 'Width of the bottom border on accordions.',
    category: 'description',
    options: 'borderWidth' as OptionsRef,
    cssVars: [
      { cssVar: 'description-collapsibles-border-width', transform: 'px', cssDefault: '1px' },
    ],
    defaultValue: '',
    resetable: true,
  },
  {
    path: 'variables.elements.description.collapsibles.border.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Accordion border radius',
    description: 'Border radius of accordions.',
    category: 'description',
    options: [
      { value: '', label: 'Auto' },
      { value: 0, label: '0px' },
      { value: 2, label: '2px' },
      { value: 4, label: '4px' },
      { value: 8, label: '8px' },
    ],
    cssVars: [
      { cssVar: 'description-collapsibles-border-radius', transform: 'px', cssDefault: '0px' },
    ],
    defaultValue: '',
    resetable: true,
  },
];

// ============================================================================
// Checkout container entries
// ============================================================================

const CHECKOUT_ENTRIES: ThemeProperty[] = [
  {
    path: 'variables.elements.checkout.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Checkout background',
    description: 'Background color of the checkout container.',
    category: 'checkout',
    cssVars: [{ cssVar: 'checkout-background-color', transform: 'direct' }],
  },
  {
    path: 'variables.elements.checkout.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Checkout text color',
    description: 'Default text color of the checkout container.',
    category: 'checkout',
    cssVars: [
      { cssVar: 'checkout-color', transform: 'direct', cssDefault: 'var(--cp_text-primary)' },
    ],
  },
  {
    path: 'variables.elements.checkout.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of the checkout container.',
    category: 'checkout',
    cssVars: [{ cssVar: 'checkout-border-color', transform: 'direct', cssDefault: 'transparent' }],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of the checkout container.',
    category: 'checkout',
    cssVars: [{ cssVar: 'checkout-border-width', transform: 'px', cssDefault: '0px' }],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of the checkout container.',
    category: 'checkout',
    cssVars: [{ cssVar: 'checkout-border-radius', transform: 'px' }],
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.styles.padding',
    cssProperty: 'padding',
    type: 'size',
    label: 'Padding',
    description: 'Padding of the checkout container.',
    category: 'checkout',
    cssVars: [{ cssVar: 'checkout-padding', transform: 'px', cssDefault: '0px' }],
    options: [
      { value: '', label: 'Auto' },
      { value: 32, label: '32px' },
      { value: 48, label: '48px' },
      { value: 64, label: '64px' },
      { value: 80, label: '80px' },
    ],
  },
  {
    path: 'variables.elements.checkout.divider.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Divider',
    description: 'Color of the divider line in the checkout.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-divider-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_border-subtle)',
      },
    ],
    resetable: true,
  },
  // --- Checkout form ---
  {
    path: 'variables.elements.checkout.form.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Form background',
    description: 'Background color of the checkout form.',
    category: 'checkout',
    cssVars: [{ cssVar: 'checkout-form-background-color', transform: 'direct' }],
  },
  {
    path: 'variables.elements.checkout.form.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Form text color',
    description: 'Default text color of the checkout form.',
    category: 'checkout',
    cssVars: [
      { cssVar: 'checkout-form-color', transform: 'direct', cssDefault: 'var(--cp_text-primary)' },
    ],
  },
];

// ============================================================================
// Field entries (label, input, focus, placeholder)
// ============================================================================

const FIELD_ENTRIES: ThemeProperty[] = [
  // --- Fields container (CSS-only) ---
  {
    path: 'variables.elements.checkout.form.fields.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Fields background',
    description: 'Background color of the fields container.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-background-color',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.form.fields.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Fields text color',
    description: 'Text color of the fields container.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
  },
  // --- Field container (CSS-only) ---
  {
    path: 'variables.elements.checkout.form.fields.field.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Field background',
    description: 'Background color of individual field wrappers.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-field-background-color',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.form.fields.field.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Field text color',
    description: 'Text color of individual field wrappers.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-field-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
  },
  // --- Label ---
  // Resting properties carry a `-set` mirror cssVar (see `withSetMirrors`) so
  // the PWYW label subsection chain (pwywLabelGroup's `setTiers`) can detect
  // "the seller explicitly set Fields > Label" and slot in below Price
  // options > Label but above the Cards-derived fallback. Focus is excluded —
  // that tier's fallback topology is unchanged (see pwywLabelGroup).
  ...withSetMirrors([
    {
      path: 'variables.elements.checkout.form.fields.label.styles.color',
      cssProperty: 'color',
      type: 'color',
      label: 'Text',
      description: 'Color of field labels.',
      category: 'fields',
      cssVars: [
        {
          cssVar: 'checkout-form-fields-label-color',
          transform: 'direct',
          cssDefault: 'var(--cp_text-primary)',
        },
      ],
      resetable: true,
    },
    {
      path: 'variables.elements.checkout.form.fields.label.styles.paddingBottom',
      cssProperty: 'paddingBottom',
      type: 'size',
      label: 'Padding bottom',
      description: 'Bottom padding below field labels.',
      category: 'fields',
      cssVars: [
        {
          cssVar: 'checkout-form-fields-label-padding-bottom',
          transform: 'px',
          cssDefault: '0.25rem',
        },
      ],
      options: 'paddingPx' as OptionsRef,
    },
    ...fontGroup(
      'variables.elements.checkout.form.fields.label.styles',
      'checkout-form-fields-label',
      'field label',
      'fields',
      { ...FONT_BODY, fontWeight: 'var(--cp_font-label-weight)', lineHeight: undefined },
    ),
  ]),
  // --- Label focus ---
  // Carries a `-set` mirror so the price-option pwywLabel focus chain can
  // follow an explicit Fields > Label focus without its resting-chain default
  // beating the option label's Cards-derived look (see pwywLabelGroup's
  // focusColorDefault).
  ...withSetMirrors([
    {
      path: 'variables.elements.checkout.form.fields.label.focusStyles.color',
      cssProperty: 'color',
      type: 'color',
      label: 'Focus color',
      description: 'Color of the field label when the input is focused.',
      category: 'fields',
      cssVars: [
        {
          cssVar: 'checkout-form-fields-label-focus-color',
          transform: 'direct',
          cssDefault: 'var(--cp_checkout-form-fields-label-color)',
        },
      ],
      resetable: true,
    },
  ]),
  // --- Input ---
  {
    path: 'variables.elements.checkout.form.fields.input.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text',
    description: 'Text color of input fields.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-input-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of input fields.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-input-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_surface)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of input fields.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-input-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_input-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of input fields.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-input-border-width',
        transform: 'px',
        cssDefault: 'var(--cp_input-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of input fields.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-input-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_input-border-radius)',
      },
    ],
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.paddingX',
    cssProperty: 'paddingX',
    type: 'size',
    label: 'Horizontal padding',
    description: 'Horizontal padding of input fields.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-input-padding-x',
        transform: 'px',
        cssDefault: 'var(--cp_input-padding-x)',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  // paddingY carries a `-set` mirror so the variant PWYW input (whose default
  // is the compact 0.375rem, not this chain) can detect an explicit Fields
  // Vertical padding and follow it — see the variant pwywInputGroup call.
  ...withSetMirrors([
    {
      path: 'variables.elements.checkout.form.fields.input.styles.paddingY',
      cssProperty: 'paddingY',
      type: 'size',
      label: 'Vertical padding',
      description: 'Vertical padding of input fields.',
      category: 'fields',
      cssVars: [
        {
          cssVar: 'checkout-form-fields-input-padding-y',
          transform: 'px',
          cssDefault: 'var(--cp_input-padding-y)',
        },
      ],
      options: 'paddingPx' as OptionsRef,
    },
  ]),
  {
    path: 'variables.elements.checkout.form.fields.input.styles.boxShadowColor',
    cssProperty: 'boxShadowColor',
    type: 'color',
    label: 'Shadow color',
    description: 'Shadow color of input fields.',
    category: 'fields',
    cssVars: [{ cssVar: 'checkout-form-fields-input-box-shadow-color', transform: 'direct' }],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.styles.boxShadow',
    cssProperty: 'boxShadow',
    type: 'shadow',
    label: 'Shadow width',
    description: 'Shadow size of input fields.',
    category: 'fields',
    cssVars: [{ cssVar: 'checkout-form-fields-input-box-shadow', transform: 'shadow' }],
    options: 'shadow' as OptionsRef,
    defaultValue: 'sm',
  },
  ...fontGroup(
    'variables.elements.checkout.form.fields.input.styles',
    'checkout-form-fields-input',
    'input field',
    'fields',
    { ...FONT_BODY, fontStyle: 'normal' },
  ),
  // --- Input focus ---
  {
    path: 'variables.elements.checkout.form.fields.input.focusStyles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Focus color',
    description:
      'Text color of input fields when focused. Only emitted when differs from input resting state.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-input-focus-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-fields-input-color)',
      },
    ],
    resetable: true,
    derived: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.focusStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Focus background',
    description:
      'Background color of input fields when focused. Only emitted when differs from input resting state.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-input-focus-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-fields-input-background-color)',
      },
    ],
    resetable: true,
    derived: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.focusStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Focus border',
    description:
      'Border color of input fields when focused. Only emitted when differs from input resting state.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-input-focus-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-fields-input-border-color)',
      },
    ],
    resetable: true,
    derived: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.focusStyles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Focus border radius',
    description: 'Border radius of input fields when focused.',
    category: 'fields',
    cssVars: [{ cssVar: 'checkout-form-fields-input-focus-border-radius', transform: 'px' }],
  },
  // --- Placeholder ---
  {
    path: 'variables.elements.checkout.form.fields.input.placeholder.enabled',
    type: 'toggle',
    label: 'Show placeholder',
    description: 'Whether input field placeholders are visible.',
    category: 'fields',
    cssVars: [],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.input.placeholderStyles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Placeholder',
    description:
      'Color of input field placeholder text. Handled by placeholder enabled toggle logic.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-input-placeholder-color',
        transform: 'direct',
        cssDefault: 'var(--cp_input-placeholder-color)',
      },
    ],
    resetable: true,
    derived: true,
  }, // --- Quantity selector: every − / + stepper; the count box follows Input until set ---
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.icon.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Icon color',
    description: 'Color of the − and + icons in quantity steppers.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-quantitySelector-icon-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.icon.styles.size',
    cssProperty: 'width',
    type: 'size',
    label: 'Icon size',
    description: 'Size of the − and + icons in quantity steppers.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-quantitySelector-icon-size',
        transform: 'px',
        cssDefault: '1.25rem',
      },
    ],
    options: 'iconSize' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background of the quantity stepper count box.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-quantitySelector-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-fields-input-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text color',
    description: 'Text color of the quantity stepper count box.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-quantitySelector-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-fields-input-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border color',
    description: 'Border color of the quantity stepper count box.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-quantitySelector-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-fields-input-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of the quantity stepper count box.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-quantitySelector-border-width',
        transform: 'px',
        cssDefault: 'var(--cp_checkout-form-fields-input-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.fields.quantitySelector.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of the quantity stepper count box.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-quantitySelector-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_checkout-form-fields-input-border-radius)',
      },
    ],
    options: 'sizePx' as OptionsRef,
  },

  // --- Description ---
  {
    path: 'variables.elements.checkout.form.fields.description.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text',
    description: 'Color of field descriptions.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-description-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.fields.description.styles.paddingTop',
    cssProperty: 'paddingTop',
    type: 'size',
    label: 'Padding top',
    description: 'Top padding above field descriptions.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-description-padding-top',
        transform: 'px',
        cssDefault: '0.25rem',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  ...fontGroup(
    'variables.elements.checkout.form.fields.description.styles',
    'checkout-form-fields-description',
    'field description',
    'fields',
    {
      ...FONT_BODY,
      fontSize: 'var(--cp_font-body-size-sm)',
      lineHeight: 'var(--cp_font-body-line-height-sm)',
    },
  ),
  // --- Field groups ---
  {
    path: 'variables.elements.checkout.form.groups.styles.gap',
    cssProperty: 'columnGap',
    type: 'size',
    label: 'Column gap',
    description: 'Space between fields that sit side by side in a group.',
    category: 'fields',
    cssVars: [{ cssVar: 'checkout-form-groups-gap', transform: 'px', cssDefault: '1rem' }],
    options: 'paddingPx' as OptionsRef,
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.groups.styles.rowGap',
    cssProperty: 'rowGap',
    type: 'size',
    label: 'Row gap',
    description: 'Space between rows of fields in a group.',
    category: 'fields',
    cssVars: [{ cssVar: 'checkout-form-groups-rowGap', transform: 'px', cssDefault: '1rem' }],
    options: 'paddingPx' as OptionsRef,
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.groups.phones',
    type: 'select',
    label: 'On phones',
    description: 'Whether fields in a group stack or stay side by side on narrow screens.',
    category: 'fields',
    cssVars: [],
    options: [
      { value: 'stack', label: 'Stack fields' },
      { value: 'keep', label: 'Keep side by side' },
    ],
    defaultValue: 'stack',
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.groups.styles.alignItems',
    cssProperty: 'alignItems',
    type: 'select',
    label: 'Align fields',
    description: 'How fields of different heights line up in a row of a group.',
    category: 'fields',
    cssVars: [
      { cssVar: 'checkout-form-groups-align-items', transform: 'direct', cssDefault: 'center' },
    ],
    options: [
      { value: 'start', label: 'Top' },
      { value: 'center', label: 'Center' },
      { value: 'end', label: 'Bottom' },
    ],
    defaultValue: 'center',
    resetable: true,
  },
  // --- Form headings ---
  {
    path: 'variables.elements.checkout.form.heading.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text',
    description: 'Color of headings in the form.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-heading-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.heading.styles.paddingBottom',
    cssProperty: 'paddingBottom',
    type: 'size',
    label: 'Padding bottom',
    description: 'Space below headings in the form.',
    category: 'fields',
    cssVars: [
      { cssVar: 'checkout-form-heading-padding-bottom', transform: 'px', cssDefault: '1rem' },
    ],
    options: 'paddingPx' as OptionsRef,
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.heading.styles',
    'checkout-form-heading',
    'form heading',
    'fields',
    {
      fontFamily: 'var(--cp_font-heading)',
      fontStyle: 'var(--cp_font-heading-style)',
      fontWeight: 'var(--cp_font-heading-weight-light)',
      fontSize: 'var(--cp_font-heading-size-h4)',
      lineHeight: 'var(--cp_font-heading-line-height-h4)',
    },
  ),
  {
    path: 'variables.elements.checkout.form.subheading.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text',
    description: 'Color of subheadings in the form.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-subheading-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.subheading.styles.paddingBottom',
    cssProperty: 'paddingBottom',
    type: 'size',
    label: 'Padding bottom',
    description: 'Space below subheadings in the form.',
    category: 'fields',
    cssVars: [
      { cssVar: 'checkout-form-subheading-padding-bottom', transform: 'px', cssDefault: '1rem' },
    ],
    options: 'paddingPx' as OptionsRef,
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.subheading.styles',
    'checkout-form-subheading',
    'form subheading',
    'fields',
    {
      fontFamily: 'var(--cp_font-heading)',
      fontStyle: 'var(--cp_font-heading-style)',
      fontWeight: 'var(--cp_font-heading-weight-light)',
      fontSize: 'var(--cp_font-heading-size-h5)',
      lineHeight: 'var(--cp_font-heading-line-height-h5)',
    },
  ), // --- Form dividers ---
  {
    path: 'variables.elements.checkout.form.divider.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Color',
    description: 'Color of dividers in the form.',
    category: 'fields',
    cssVars: [
      {
        cssVar: 'checkout-form-divider-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-fields-input-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.divider.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Thickness',
    description: 'Thickness of dividers in the form.',
    category: 'fields',
    cssVars: [{ cssVar: 'checkout-form-divider-thickness', transform: 'px', cssDefault: '1px' }],
    options: 'borderWidth' as OptionsRef,
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.divider.styles.borderStyle',
    cssProperty: 'borderStyle',
    type: 'select',
    label: 'Style',
    description: 'Line style of dividers in the form.',
    category: 'fields',
    cssVars: [{ cssVar: 'checkout-form-divider-style', transform: 'direct', cssDefault: 'solid' }],
    options: 'borderStyle' as OptionsRef,
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.divider.styles.width',
    cssProperty: 'width',
    type: 'select',
    label: 'Width',
    description: 'How far dividers reach across the form.',
    category: 'fields',
    cssVars: [{ cssVar: 'checkout-form-divider-width', transform: 'direct', cssDefault: '100%' }],
    options: [
      { value: '', label: 'Auto' },
      { value: '100%', label: 'Full width' },
      { value: '50%', label: 'Half width' },
      { value: '4rem', label: 'Short' },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.divider.styles.marginTop',
    cssProperty: 'marginTop',
    type: 'size',
    label: 'Space above',
    description: 'Space above dividers in the form.',
    category: 'fields',
    cssVars: [{ cssVar: 'checkout-form-divider-margin-top', transform: 'px', cssDefault: '0px' }],
    options: 'paddingPx' as OptionsRef,
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.divider.styles.marginBottom',
    cssProperty: 'marginBottom',
    type: 'size',
    label: 'Space below',
    description: 'Space below dividers in the form.',
    category: 'fields',
    cssVars: [
      { cssVar: 'checkout-form-divider-margin-bottom', transform: 'px', cssDefault: '1rem' },
    ],
    options: 'paddingPx' as OptionsRef,
    resetable: true,
  },
];

// ============================================================================
// Button entries
// ============================================================================

const BUTTON_ENTRIES: ThemeProperty[] = [
  // --- Button styles ---
  {
    path: 'variables.elements.checkout.form.button.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text',
    description: 'Text color of the submit button.',
    category: 'button',
    cssVars: [
      {
        cssVar: 'checkout-form-button-color',
        transform: 'direct',
        cssDefault: 'var(--color-white)',
      },
      {
        cssVar: 'submit-button-text-color',
        transform: 'direct',
        note: 'derived from colorPrimary, overridden by explicit value',
        cssDefault: 'var(--color-white)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.button.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of the submit button.',
    category: 'button',
    cssVars: [
      {
        cssVar: 'checkout-form-button-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_color-primary)',
      },
      {
        cssVar: 'submit-button-background-color',
        transform: 'direct',
        note: 'derived from colorPrimary, overridden by explicit value',
        cssDefault: 'var(--cp_color-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.button.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of the submit button.',
    category: 'button',
    cssVars: [
      {
        cssVar: 'checkout-form-button-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_color-primary)',
      },
      {
        cssVar: 'submit-button-border-color',
        transform: 'direct',
        note: 'derived from colorPrimary, overridden by explicit value',
        cssDefault: 'var(--cp_color-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.button.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of the submit button.',
    category: 'button',
    cssVars: [
      {
        cssVar: 'checkout-form-button-border-width',
        transform: 'px',
        cssDefault: 'var(--cp_button-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.button.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of the submit button.',
    category: 'button',
    cssVars: [
      {
        cssVar: 'checkout-form-button-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_button-border-radius)',
      },
    ],
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.button.styles.padding',
    cssProperty: 'padding',
    type: 'size',
    label: 'Padding',
    description: 'Padding of the submit button.',
    category: 'button',
    cssVars: [
      {
        cssVar: 'checkout-form-button-padding',
        transform: 'px',
        cssDefault: 'var(--cp_button-padding, 0.75rem 0.75rem)',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  // --- Button font (uses submit-button-* CSS vars) ---
  ...fontGroup(
    'variables.elements.checkout.form.button.styles',
    'submit-button',
    'submit button',
    'button',
    { ...FONT_BODY, fontWeight: 'calc(var(--cp_font-body-weight) + 100)', lineHeight: undefined },
  ),
  // --- Button hover ---
  {
    path: 'variables.elements.checkout.form.button.hoverStyles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text hover',
    description: 'Text color of the submit button on hover.',
    category: 'button',
    cssVars: [
      {
        cssVar: 'submit-button-text-color-hover',
        transform: 'direct',
        note: 'derived from colorPrimary, overridden by explicit value',
        cssDefault: 'var(--color-white)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.button.hoverStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background hover',
    description: 'Background color of the submit button on hover.',
    category: 'button',
    cssVars: [
      {
        cssVar: 'checkout-form-button-background-color-hover',
        transform: 'direct',
        cssDefault: 'var(--cp_color-primary-dark)',
      },
      {
        cssVar: 'submit-button-background-color-hover',
        transform: 'direct',
        note: 'derived from colorPrimary, overridden by explicit value',
        cssDefault: 'var(--cp_color-primary-dark)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.button.hoverStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border hover',
    description: 'Border color of the submit button on hover.',
    category: 'button',
    cssVars: [
      {
        cssVar: 'submit-button-border-color-hover',
        transform: 'direct',
        note: 'derived from colorPrimary, overridden by explicit value',
        cssDefault: 'var(--cp_color-primary-dark)',
      },
    ],
    resetable: true,
  },
];

// ============================================================================
// Card entries (shared defaults for variants, ticket types, manual payments)
// ============================================================================

const CARD_ENTRIES: ThemeProperty[] = [
  // --- Container ---
  {
    path: 'variables.elements.checkout.cards.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Default background color for selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_surface)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.cards.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Default border color for selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_border-default)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.cards.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Default border width for selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-border-width',
        transform: 'px',
        cssDefault: '1px',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Default border radius for selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-border-radius',
        transform: 'px',
      },
    ],
    options: 'sizePx' as OptionsRef,
  },
  // --- Selected ---
  {
    path: 'variables.elements.checkout.cards.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Default background color for selected cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_surface-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.cards.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description: 'Default border color for selected cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-border-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_color-secondary)',
      },
    ],
    resetable: true,
  },
  // --- Group name ---
  {
    path: 'variables.elements.checkout.cards.groupName.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Group name background',
    description: 'Default group name background color for selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-groupName-background-color',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
  },
  // --- Name ---
  {
    path: 'variables.elements.checkout.cards.name.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Name',
    description: 'Default name color for selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-name-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup('variables.elements.checkout.cards.name.styles', 'card-name', 'card name', 'cards', {
    ...FONT_BODY,
    fontWeight: 'calc(var(--cp_font-body-weight) + 200)',
    lineHeight: undefined,
  }),
  // --- Description ---
  {
    path: 'variables.elements.checkout.cards.description.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Description',
    description: 'Default description color for selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-description-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.cards.description.styles',
    'card-description',
    'card description',
    'cards',
    { ...FONT_BODY, lineHeight: undefined },
  ),
  // --- Price ---
  {
    path: 'variables.elements.checkout.cards.price.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Price',
    description: 'Default price color for selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-price-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.cards.price.styles',
    'card-price',
    'card price',
    'cards',
    { ...FONT_BODY, fontWeight: 'calc(var(--cp_font-body-weight) + 100)', lineHeight: undefined },
  ),
  // --- Discounted from price ---
  {
    path: 'variables.elements.checkout.cards.discountedFromPrice.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Discounted from price',
    description: 'Default discounted from price color for selectable cards.',
    category: 'cards',
    cssVars: [{ cssVar: 'card-discountedFromPrice-color', transform: 'direct' }],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.cards.discountedFromPrice.styles',
    'card-discountedFromPrice',
    'card discounted from price',
    'cards',
    { ...FONT_BODY, lineHeight: undefined },
  ),
  // --- Detail (shared muted text color) ---
  {
    path: 'variables.elements.checkout.cards.detail.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Detail',
    description:
      'Default color for secondary text on cards (descriptions, remaining, dates, etc.).',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-detail-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.cards.groupName.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Group name',
    description: 'Default group name color for selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-groupName-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.cards.groupName.styles.paddingBottom',
    cssProperty: 'paddingBottom',
    type: 'size',
    label: 'Padding bottom',
    description: 'Default bottom padding below card group names.',
    category: 'cards',
    cssVars: [{ cssVar: 'card-groupName-padding-bottom', transform: 'px', cssDefault: '0.25rem' }],
    options: 'paddingPx' as OptionsRef,
  },
  ...fontGroup(
    'variables.elements.checkout.cards.groupName.styles',
    'card-groupName',
    'card group name',
    'cards',
    { ...FONT_BODY, fontWeight: 'calc(var(--cp_font-body-weight) + 200)', lineHeight: undefined },
  ),
  // --- Highlight (the pill on a price option or an order bump) ---
  {
    path: 'variables.elements.checkout.cards.badge.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Highlight text',
    description: 'Default text color of the highlight pill on selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-badge-color',
        transform: 'direct',
        // White in both modes: the pill sits on the secondary accent, usually
        // dark. CheckoutCSS re-derives this against the effective background.
        cssDefault: 'var(--color-white)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Highlight background',
    description: 'Default background color of the highlight pill on selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-badge-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_color-secondary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Highlight border',
    description: 'Default border color of the highlight pill on selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-badge-border-color',
        transform: 'direct',
        // Defaults to the highlight background so the border is invisible until
        // a seller picks a contrasting colour.
        cssDefault: 'var(--cp_card-badge-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Highlight border width',
    description: 'Default border width of the highlight pill on selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-badge-border-width',
        transform: 'px',
        cssDefault: '0px',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Highlight border radius',
    description: 'Default border radius of the highlight pill on selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-badge-border-radius',
        transform: 'px',
        // Follows the global Border radius token via the page-level var, NOT the
        // Cards > Card radius, so a card radius override does not drag the pill
        // along; overridable per the badgeRadius set.
        cssDefault: 'var(--cp_checkout-border-radius)',
      },
    ],
    options: 'badgeRadius' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.cards.badge.styles.padding',
    cssProperty: 'padding',
    type: 'size',
    label: 'Highlight padding',
    description: 'Default padding inside the highlight pill on selectable cards.',
    category: 'cards',
    cssVars: [
      {
        cssVar: 'card-badge-padding',
        transform: 'px',
        cssDefault: '0.125rem 0.5rem',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  ...fontGroup(
    'variables.elements.checkout.cards.badge.styles',
    'card-badge',
    'card highlight',
    'cards',
    {
      fontFamily: 'var(--cp_font-body)',
      fontStyle: 'var(--cp_font-body-style)',
      fontWeight: 'var(--cp_font-body-weight)',
      fontSize: 'var(--cp_font-body-size-sm)',
      lineHeight: '1.4',
    },
  ),
];

// ============================================================================
// Variant entries
// ============================================================================

const VARIANT_ENTRIES: ThemeProperty[] = [
  {
    path: 'variables.elements.checkout.variants.position',
    type: 'select',
    label: 'Position',
    description: 'Where product variants are displayed.',
    category: 'variants',
    cssVars: [],
    options: [
      { value: 'form', label: 'Form' },
      { value: 'content', label: 'Content' },
    ],
  },
  // --- VariantOptions container ---
  {
    path: 'variables.elements.checkout.variants.variantOptions.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of variant option cards.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of variant option cards.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of variant option cards.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-border-width',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of variant option cards.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-radius)',
      },
    ],
    options: 'sizePx' as OptionsRef,
  },
  // --- VariantOptions selected ---
  {
    path: 'variables.elements.checkout.variants.variantOptions.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Background color of the selected variant option.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description: 'Border color of the selected variant option.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-border-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color-selected)',
      },
    ],
    resetable: true,
  },
  // --- Variant name ---
  {
    path: 'variables.elements.checkout.variants.name.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Variant name background',
    description: 'Background color of the variant group name.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-name-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-groupName-background-color)',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.variants.name.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Variant name',
    description: 'Color of the variant group name.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-name-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-groupName-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.variants.name.styles.paddingBottom',
    cssProperty: 'paddingBottom',
    type: 'size',
    label: 'Padding bottom',
    description: 'Bottom padding below the variant group name.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-name-padding-bottom',
        transform: 'px',
        cssDefault: 'var(--cp_card-groupName-padding-bottom)',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  ...fontGroup(
    'variables.elements.checkout.variants.name.styles',
    'checkout-variants-name',
    'variant name',
    'variants',
    {
      fontFamily: 'var(--cp_card-groupName-font-family)',
      fontStyle: 'var(--cp_card-groupName-font-style)',
      fontWeight: 'var(--cp_card-groupName-font-weight)',
      fontSize: 'var(--cp_card-groupName-font-size)',
    },
  ),
  // --- VariantOptions name ---
  {
    path: 'variables.elements.checkout.variants.variantOptions.name.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Option name background',
    description: 'Background color of the variant option name.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-name-background-color',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.name.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Option name',
    description: 'Color of the variant option name.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-name-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-name-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.variants.variantOptions.name.styles',
    'checkout-variants-variantOptions-name',
    'variant option name',
    'variants',
    {
      fontFamily: 'var(--cp_card-name-font-family)',
      fontStyle: 'var(--cp_card-name-font-style)',
      fontWeight: 'var(--cp_card-name-font-weight)',
      fontSize: 'var(--cp_card-name-font-size)',
    },
  ),
  // --- VariantOptions description ---
  {
    path: 'variables.elements.checkout.variants.variantOptions.description.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Description background',
    description: 'Background color of the variant option description.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-description-background-color',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.description.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Description',
    description: 'Color of the variant option description.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-description-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-description-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.variants.variantOptions.description.styles',
    'checkout-variants-variantOptions-description',
    'variant option description',
    'variants',
    {
      fontFamily: 'var(--cp_card-description-font-family)',
      fontStyle: 'var(--cp_card-description-font-style)',
      fontWeight: 'var(--cp_card-description-font-weight)',
      fontSize: 'var(--cp_card-description-font-size)',
    },
  ),
  // --- VariantOptions price ---
  {
    path: 'variables.elements.checkout.variants.variantOptions.price.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Price background',
    description: 'Background color of the variant option price.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-price-background-color',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.variants.variantOptions.price.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Price',
    description: 'Color of the variant option price.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-price-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-price-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.variants.variantOptions.price.styles',
    'checkout-variants-variantOptions-price',
    'variant option price',
    'variants',
    {
      fontFamily: 'var(--cp_card-price-font-family)',
      fontStyle: 'var(--cp_card-price-font-style)',
      fontWeight: 'var(--cp_card-price-font-weight)',
      fontSize: 'var(--cp_card-price-font-size)',
    },
  ),
  // --- VariantOptions discountedFromPrice ---
  {
    path: 'variables.elements.checkout.variants.variantOptions.discountedFromPrice.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Discounted from price',
    description: 'Color of the original price on variant options.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-discountedFromPrice-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-discountedFromPrice-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.variants.variantOptions.discountedFromPrice.styles',
    'checkout-variants-variantOptions-discountedFromPrice',
    'variant discounted from price',
    'variants',
    {
      fontFamily: 'var(--cp_card-discountedFromPrice-font-family)',
      fontStyle: 'var(--cp_card-discountedFromPrice-font-style)',
      fontWeight: 'var(--cp_card-discountedFromPrice-font-weight)',
      fontSize: 'var(--cp_card-discountedFromPrice-font-size)',
    },
  ),
  // --- VariantOptions planIterations ---
  {
    path: 'variables.elements.checkout.variants.variantOptions.planIterations.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Number of payments',
    description: 'Color of the plan iterations text on variant options.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-planIterations-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-detail-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.variants.variantOptions.planIterations.styles',
    'checkout-variants-variantOptions-planIterations',
    'variant plan iterations',
    'variants',
    {
      ...FONT_BODY,
      fontSize: 'var(--cp_font-body-size-sm)',
      lineHeight: 'var(--cp_font-body-line-height-sm)',
    },
  ),
  // --- VariantOptions plusSign ---
  {
    path: 'variables.elements.checkout.variants.variantOptions.plusSign.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Plus sign',
    description: 'Color of the plus sign between variant options.',
    category: 'variants',
    cssVars: [
      {
        cssVar: 'checkout-variants-variantOptions-plusSign-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  ...pwywInputGroup(
    'variables.elements.checkout.variants.variantOptions.pwywInput',
    'checkout-variants-variantOptions-pwywInput',
    {
      category: 'variants',
      defaults: {
        paddingY:
          'var(--cp_pwyw-input-padding-y-set, var(--cp_checkout-form-fields-input-padding-y-set, 0.375rem))',
      },
      chainTo: 'pwyw-input',
    },
  ),
  ...pwywSuffixGroup(
    'variables.elements.checkout.variants.variantOptions.pwywSuffix',
    'checkout-variants-variantOptions-pwywSuffix',
    'variants',
  ),
];

// ============================================================================
// Status badge entries (shared by ticket types and variants)
// ============================================================================

const STATUS_BADGE_ENTRIES: ThemeProperty[] = [
  ...fontGroup(
    'variables.elements.checkout.statusBadges.styles',
    'checkout-statusBadges',
    'status badge',
    'statusBadges',
    { ...FONT_BODY, fontWeight: '700', fontSize: '0.75rem', lineHeight: undefined },
  ),
  // Per-badge colors
  {
    path: 'variables.elements.checkout.statusBadges.soldOutColor',
    cssProperty: 'color',
    type: 'color',
    label: 'Sold out',
    description: 'Text color of the sold out badge.',
    category: 'statusBadges',
    cssVars: [
      {
        cssVar: 'checkout-statusBadges-soldOut-color',
        transform: 'direct',
        cssDefault: 'var(--color-white)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.statusBadges.soldOutBackgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of the sold out badge.',
    category: 'statusBadges',
    cssVars: [
      {
        cssVar: 'checkout-statusBadges-soldOut-background-color',
        transform: 'direct',
        cssDefault: 'var(--color-gray-900)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.statusBadges.canceledColor',
    cssProperty: 'color',
    type: 'color',
    label: 'Canceled',
    description: 'Text color of the canceled badge.',
    category: 'statusBadges',
    cssVars: [
      {
        cssVar: 'checkout-statusBadges-canceled-color',
        transform: 'direct',
        cssDefault: 'var(--color-white)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.statusBadges.canceledBackgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of the canceled badge.',
    category: 'statusBadges',
    cssVars: [
      {
        cssVar: 'checkout-statusBadges-canceled-background-color',
        transform: 'direct',
        cssDefault: 'var(--color-red-500)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.statusBadges.unavailableColor',
    cssProperty: 'color',
    type: 'color',
    label: 'Unavailable',
    description: 'Text color of the unavailable badge.',
    category: 'statusBadges',
    cssVars: [
      {
        cssVar: 'checkout-statusBadges-unavailable-color',
        transform: 'direct',
        cssDefault: 'var(--color-white)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.statusBadges.unavailableBackgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of the unavailable badge.',
    category: 'statusBadges',
    cssVars: [
      {
        cssVar: 'checkout-statusBadges-unavailable-background-color',
        transform: 'direct',
        cssDefault: 'var(--color-gray-500)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.statusBadges.onSaleSoonColor',
    cssProperty: 'color',
    type: 'color',
    label: 'On sale soon',
    description: 'Text color of the on sale soon badge.',
    category: 'statusBadges',
    cssVars: [
      {
        cssVar: 'checkout-statusBadges-onSaleSoon-color',
        transform: 'direct',
        cssDefault: 'var(--color-white)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.statusBadges.onSaleSoonBackgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of the on sale soon badge.',
    category: 'statusBadges',
    cssVars: [
      {
        cssVar: 'checkout-statusBadges-onSaleSoon-background-color',
        transform: 'direct',
        cssDefault: 'var(--color-green-300)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.statusBadges.notOnSaleColor',
    cssProperty: 'color',
    type: 'color',
    label: 'Not on sale',
    description: 'Text color of the not on sale badge.',
    category: 'statusBadges',
    cssVars: [
      {
        cssVar: 'checkout-statusBadges-notOnSale-color',
        transform: 'direct',
        cssDefault: 'var(--color-white)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.statusBadges.notOnSaleBackgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of the not on sale badge.',
    category: 'statusBadges',
    cssVars: [
      {
        cssVar: 'checkout-statusBadges-notOnSale-background-color',
        transform: 'direct',
        cssDefault: 'var(--color-gray-500)',
      },
    ],
    resetable: true,
  },
];

// ============================================================================
// Ticket group entries
// ============================================================================

const TICKET_GROUP_ENTRIES: ThemeProperty[] = [
  {
    path: 'variables.elements.checkout.ticketGroups.position',
    type: 'select',
    label: 'Position',
    description: 'Where ticket types are displayed.',
    category: 'ticketGroups',
    cssVars: [],
    options: [
      { value: 'form', label: 'Form' },
      { value: 'content', label: 'Content' },
    ],
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.quantitySelector',
    type: 'select',
    label: 'Selector',
    description: 'How buyers pick a ticket count: a dropdown or a − / + stepper.',
    category: 'ticketGroups',
    cssVars: [],
    defaultValue: 'dropdown',
    options: [
      { value: 'dropdown', label: 'Dropdown' },
      { value: 'stepper', label: 'Stepper' },
    ],
  },
  // --- TicketTypes container ---
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of ticket type cards.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of ticket type cards.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of ticket type cards.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-border-width',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of ticket type cards.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-radius)',
      },
    ],
    options: 'sizePx' as OptionsRef,
    resetable: true,
  },
  // --- TicketTypes selected ---
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Background color of the selected ticket type.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color-selected)',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description: 'Border color of the selected ticket type.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-border-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color-selected)',
      },
    ],
  },
  // --- Ticket group name ---
  {
    path: 'variables.elements.checkout.ticketGroups.name.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Group name background',
    description: 'Background color of the ticket group name.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-name-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-groupName-background-color)',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.ticketGroups.name.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Group name',
    description: 'Color of the ticket group name.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-name-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-groupName-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.ticketGroups.name.styles.paddingBottom',
    cssProperty: 'paddingBottom',
    type: 'size',
    label: 'Padding bottom',
    description: 'Bottom padding below the ticket group name.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-name-padding-bottom',
        transform: 'px',
        cssDefault: 'var(--cp_card-groupName-padding-bottom)',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  ...fontGroup(
    'variables.elements.checkout.ticketGroups.name.styles',
    'checkout-ticketGroups-name',
    'ticket group name',
    'ticketGroups',
    {
      fontFamily: 'var(--cp_card-groupName-font-family)',
      fontStyle: 'var(--cp_card-groupName-font-style)',
      fontWeight: 'var(--cp_card-groupName-font-weight)',
      fontSize: 'var(--cp_card-groupName-font-size)',
    },
  ),
  // --- Ticket group description ---
  {
    path: 'variables.elements.checkout.ticketGroups.description.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Group description background',
    description: 'Background color of the ticket group description.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-description-background-color',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.ticketGroups.description.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Group description',
    description: 'Color of the ticket group description.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-description-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-detail-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.ticketGroups.description.styles',
    'checkout-ticketGroups-description',
    'ticket group description',
    'ticketGroups',
    { ...FONT_BODY, fontSize: 'var(--cp_font-body-size)', lineHeight: undefined },
  ),
  // --- TicketTypes name ---
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.name.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Name',
    description: 'Color of the ticket type name.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-name-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-name-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.ticketGroups.ticketTypes.name.styles',
    'checkout-ticketGroups-ticketTypes-name',
    'ticket type name',
    'ticketGroups',
    {
      fontFamily: 'var(--cp_card-name-font-family)',
      fontStyle: 'var(--cp_card-name-font-style)',
      fontWeight: 'var(--cp_card-name-font-weight)',
      fontSize: 'var(--cp_card-name-font-size)',
    },
  ),
  // --- TicketTypes description ---
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.description.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Description',
    description: 'Color of the ticket type description.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-description-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-description-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.ticketGroups.ticketTypes.description.styles',
    'checkout-ticketGroups-ticketTypes-description',
    'ticket type description',
    'ticketGroups',
    {
      fontFamily: 'var(--cp_card-description-font-family)',
      fontStyle: 'var(--cp_card-description-font-style)',
      fontWeight: 'var(--cp_card-description-font-weight)',
      fontSize: 'var(--cp_card-description-font-size)',
    },
  ),
  // --- TicketTypes price ---
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.price.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Price',
    description: 'Color of the ticket type price.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-price-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-price-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.ticketGroups.ticketTypes.price.styles',
    'checkout-ticketGroups-ticketTypes-price',
    'ticket type price',
    'ticketGroups',
    {
      fontFamily: 'var(--cp_card-price-font-family)',
      fontStyle: 'var(--cp_card-price-font-style)',
      fontWeight: 'var(--cp_card-price-font-weight)',
      fontSize: 'var(--cp_card-price-font-size)',
    },
  ),
  // --- TicketTypes discountedFromPrice ---
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.discountedFromPrice.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Discounted from price',
    description: 'Color of the original price on ticket types.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-discountedFromPrice-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-discountedFromPrice-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.ticketGroups.ticketTypes.discountedFromPrice.styles',
    'checkout-ticketGroups-ticketTypes-discountedFromPrice',
    'ticket type discounted from price',
    'ticketGroups',
    {
      fontFamily: 'var(--cp_card-discountedFromPrice-font-family)',
      fontStyle: 'var(--cp_card-discountedFromPrice-font-style)',
      fontWeight: 'var(--cp_card-discountedFromPrice-font-weight)',
      fontSize: 'var(--cp_card-discountedFromPrice-font-size)',
    },
  ),
  // Status badges moved to STATUS_BADGE_ENTRIES
  // --- TicketTypes booking fee ---
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.fee.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Booking fee',
    description: 'Color of the booking fee note under a ticket price.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-fee-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.ticketGroups.ticketTypes.fee.styles',
    'checkout-ticketGroups-ticketTypes-fee',
    'ticket type booking fee note',
    'ticketGroups',
    { ...FONT_BODY, lineHeight: undefined },
  ),
  // --- TicketTypes remaining ---
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.remaining.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Remaining',
    description: 'Color of the remaining quantity text on ticket types.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-remaining-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-detail-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.ticketGroups.ticketTypes.remaining.styles',
    'checkout-ticketGroups-ticketTypes-remaining',
    'ticket type remaining text',
    'ticketGroups',
    { ...FONT_BODY, fontSize: 'var(--cp_font-body-size-sm)', lineHeight: undefined },
  ),
  // --- TicketTypes saleDate ---
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.saleDate.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Sale date',
    description: 'Color of the sale date text on ticket types.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-saleDate-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-detail-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.ticketGroups.ticketTypes.saleDate.styles',
    'checkout-ticketGroups-ticketTypes-saleDate',
    'ticket type sale date',
    'ticketGroups',
    { ...FONT_BODY, fontSize: 'var(--cp_font-body-size-sm)', lineHeight: undefined },
  ),
  // --- TicketTypes total ---
  {
    path: 'variables.elements.checkout.ticketGroups.ticketTypes.total.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Total price',
    description: 'Color of the total price text on ticket types.',
    category: 'ticketGroups',
    cssVars: [
      {
        cssVar: 'checkout-ticketGroups-ticketTypes-total-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-detail-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.ticketGroups.ticketTypes.total.styles',
    'checkout-ticketGroups-ticketTypes-total',
    'ticket type total price',
    'ticketGroups',
    { ...FONT_BODY, fontSize: 'var(--cp_font-body-size-sm)', lineHeight: undefined },
  ),
];

// ============================================================================
// Discount code entries
// ============================================================================

const DISCOUNT_CODE_ENTRIES: ThemeProperty[] = [
  {
    path: 'variables.elements.checkout.form.discountCode.link.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Link',
    description: 'Color of the discount code link.',
    category: 'discountCode',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-discountCode-link-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
      {
        cssVar: 'checkout-form-fields-discountCode-link-color-hover',
        transform: 'direct',
        note: 'hover uses same value',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.discountCode.applyButton.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Apply button',
    description: 'Color of the discount code apply button.',
    category: 'discountCode',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-discountCode-applyButton-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-strong)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.discountCode.code.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Discount code',
    description: 'Text color of the applied discount code.',
    category: 'discountCode',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-discountCode-code-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-strong)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.discountCode.code.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Discount code background',
    description: 'Background color of the applied discount code.',
    category: 'discountCode',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-discountCode-code-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_border-default)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.discountCode.discount.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Discount',
    description: 'Color of the discount amount text.',
    category: 'discountCode',
    cssVars: [
      {
        cssVar: 'checkout-form-fields-discountCode-discount-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-strong)',
      },
    ],
    resetable: true,
  },
];

// ============================================================================
// Manual payments entries
// ============================================================================

const PAYMENT_ENTRIES: ThemeProperty[] = [
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of manual payment options.',
    category: 'payments',
    cssVars: [
      {
        cssVar: 'checkout-form-manualPayments-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text color',
    description: 'Text color of manual payment options.',
    category: 'payments',
    cssVars: [
      {
        cssVar: 'checkout-form-manualPayments-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of manual payment options.',
    category: 'payments',
    cssVars: [
      {
        cssVar: 'checkout-form-manualPayments-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of manual payment options.',
    category: 'payments',
    cssVars: [
      {
        cssVar: 'checkout-form-manualPayments-border-width',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of manual payment options.',
    category: 'payments',
    cssVars: [
      {
        cssVar: 'checkout-form-manualPayments-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-radius)',
      },
    ],
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.styles.padding',
    cssProperty: 'padding',
    type: 'size',
    label: 'Padding',
    description: 'Padding of manual payment options.',
    category: 'payments',
    cssVars: [
      { cssVar: 'checkout-form-manualPayments-padding', transform: 'px', cssDefault: '0.5rem' },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  // --- Selected state ---
  {
    path: 'variables.elements.checkout.form.manualPayments.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Background color of the selected manual payment option.',
    category: 'payments',
    cssVars: [
      {
        cssVar: 'checkout-form-manualPayments-selected-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description: 'Border color of the selected manual payment option.',
    category: 'payments',
    cssVars: [
      {
        cssVar: 'checkout-form-manualPayments-selected-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.selectedStyles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Selected text color',
    description: 'Text color of the selected manual payment option.',
    category: 'payments',
    cssVars: [
      {
        cssVar: 'checkout-form-manualPayments-selected-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.form.manualPayments.selectedStyles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Selected border width',
    description: 'Border width of the selected manual payment option.',
    category: 'payments',
    cssVars: [{ cssVar: 'checkout-form-manualPayments-selected-border-width', transform: 'px' }],
  },
  // --- Manual payments name ---
  {
    path: 'variables.elements.checkout.form.manualPayments.name.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Name',
    description: 'Color of the manual payment name.',
    category: 'payments',
    cssVars: [
      {
        cssVar: 'checkout-form-manualPayments-name-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-name-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.manualPayments.name.styles',
    'checkout-form-manualPayments-name',
    'manual payment name',
    'payments',
    {
      fontFamily: 'var(--cp_card-name-font-family)',
      fontStyle: 'var(--cp_card-name-font-style)',
      fontWeight: 'var(--cp_card-name-font-weight)',
      fontSize: 'var(--cp_card-name-font-size)',
    },
  ),
  // --- Manual payments description ---
  {
    path: 'variables.elements.checkout.form.manualPayments.description.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Description',
    description: 'Color of the manual payment description.',
    category: 'payments',
    cssVars: [
      {
        cssVar: 'checkout-form-manualPayments-description-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-description-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.manualPayments.description.styles',
    'checkout-form-manualPayments-description',
    'manual payment description',
    'payments',
    {
      fontFamily: 'var(--cp_card-description-font-family)',
      fontStyle: 'var(--cp_card-description-font-style)',
      fontWeight: 'var(--cp_card-description-font-weight)',
      fontSize: 'var(--cp_card-description-font-size)',
    },
  ),
  // --- Manual payments instructions ---
  {
    path: 'variables.elements.checkout.form.manualPayments.instructions.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Instructions',
    description: 'Color of the manual payment instructions.',
    category: 'payments',
    cssVars: [
      {
        cssVar: 'checkout-form-manualPayments-instructions-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-detail-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.manualPayments.instructions.styles',
    'checkout-form-manualPayments-instructions',
    'manual payment instructions',
    'payments',
    { ...FONT_BODY, fontSize: 'var(--cp_font-body-size-sm)', lineHeight: undefined },
  ),
];

// ============================================================================
// Price breakdown entries
// ============================================================================

const PRICE_BREAKDOWN_ENTRIES: ThemeProperty[] = [
  {
    path: 'variables.elements.checkout.form.priceBreakdown.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of the price breakdown section.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-background-color',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text',
    description: 'Default text color of the price breakdown section.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
  },
  ...fontGroup(
    'variables.elements.checkout.form.priceBreakdown.styles',
    'checkout-form-priceBreakdown',
    'price breakdown',
    'priceBreakdown',
    {
      ...FONT_BODY,
      fontSize: 'var(--cp_font-body-size)',
      lineHeight: 'var(--cp_font-body-line-height)',
    },
  ),
  {
    path: 'variables.elements.checkout.form.priceBreakdown.styles.alignment',
    cssProperty: 'alignment',
    type: 'alignment',
    label: 'Alignment',
    description: 'Text alignment of the price breakdown. Maps to flexbox justify/align values.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-justify',
        transform: 'direct',
        note: 'derived: alignment to flexbox justify',
        cssDefault: 'end',
      },
      {
        cssVar: 'checkout-form-priceBreakdown-items-align',
        transform: 'direct',
        note: 'derived: alignment to flexbox align-items',
        cssDefault: 'end',
      },
    ],
    options: [
      { value: '', label: 'Auto' },
      { value: 'left', label: 'Left' },
      { value: 'center', label: 'Center' },
      { value: 'right', label: 'Right' },
      { value: 'justify', label: 'Justify' },
    ],
    defaultValue: '',
    derived: true,
    resetable: true,
  },
  // --- Line items ---
  {
    path: 'variables.elements.checkout.form.priceBreakdown.subtotal.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Subtotal',
    description: 'Color of the subtotal line.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-subtotal-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.orderBump.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Order bump',
    description: 'Color of the order bump lines.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-orderBump-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.ticket.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Ticket',
    description: 'Color of the per-ticket lines shown when a ticket carries a fee.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-ticket-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.ticketFee.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Booking fee',
    description: 'Color of the booking fee lines.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-ticketFee-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.setupFee.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Setup fee',
    description: 'Color of the setup fee line.',
    category: 'priceBreakdown',

    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-setupFee-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.bulkDiscount.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Bulk discount',
    description: 'Color of the bulk discount line.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-bulkDiscount-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.couponDiscount.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Coupon discount',
    description: 'Color of the coupon discount line.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-couponDiscount-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.fees.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Fees',
    description: 'Color of the fees line.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-fees-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.processingFeeContribution.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Processing fee contribution',
    description: 'Color of the processing fee contribution line.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-processingFeeContribution-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.tax.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Tax',
    description: 'Color of the tax line.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-tax-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.startDate.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Start date',
    description: 'Color of the start date line.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-startDate-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.trial.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Trial',
    description: 'Color of the trial line.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-trial-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.dueNow.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Due now',
    description: 'Color of the due now line.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-dueNow-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.priceBreakdown.proration.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Proration',
    description: 'Color of the proration line.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-proration-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  // --- Total ---
  {
    path: 'variables.elements.checkout.form.priceBreakdown.total.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Total',
    description: 'Color of the total line.',
    category: 'priceBreakdown',
    cssVars: [
      {
        cssVar: 'checkout-form-priceBreakdown-total-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-priceBreakdown-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.priceBreakdown.total.styles',
    'checkout-form-priceBreakdown-total',
    'price breakdown total',
    'priceBreakdown',
    {
      fontFamily: 'var(--cp_checkout-form-priceBreakdown-font-family)',
      fontStyle: 'var(--cp_checkout-form-priceBreakdown-font-style)',
      fontWeight: 'var(--cp_checkout-form-priceBreakdown-font-weight)',
      fontSize: 'var(--cp_checkout-form-priceBreakdown-font-size)',
      lineHeight: 'var(--cp_checkout-form-priceBreakdown-line-height)',
    },
  ),
];

// ============================================================================
// Cover processing fees entries (the opt-in box above the price breakdown)
// ============================================================================

const COVER_PROCESSING_FEES_ENTRIES: ThemeProperty[] = [
  {
    // The same choices as an order bump's selector, minus the ones that read as an offer; a real
    // checkbox sits behind each so the card's <label> toggles it.
    path: 'variables.elements.checkout.form.coverProcessingFees.selector',
    type: 'select',
    label: 'Selector',
    description: 'What the customer ticks to cover processing fees.',
    category: 'coverProcessingFees',
    cssVars: [],
    defaultValue: 'checkbox',
    options: [
      { value: 'checkbox', label: 'Checkbox' },
      { value: 'switch', label: 'Switch' },
      { value: 'mark', label: 'Mark' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of the cover processing fees box.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Background color of the cover processing fees box once ticked.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of the cover processing fees box.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description: 'Border color of the cover processing fees box once ticked.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-border-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of the cover processing fees box.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-border-width',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of the cover processing fees box.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-radius)',
      },
    ],
    options: 'sizePx' as OptionsRef,
    resetable: true,
  },
  // These rest on what the order bump's controls rest on, never on the bump's own:
  // styling one must not restyle the other.
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.size',
    cssProperty: 'size',
    type: 'size',
    label: 'Size',
    description: 'Size of the cover processing fees checkbox.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-checkbox-size',
        transform: 'px',
        cssDefault: '18px',
      },
    ],
    options: 'indicatorSize' as OptionsRef,
    defaultValue: '',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background of the cover processing fees checkbox.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-checkbox-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-fields-input-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Background of the cover processing fees checkbox once ticked.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-checkbox-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_color-secondary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of the cover processing fees checkbox.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-checkbox-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-fields-input-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description: 'Border color of the cover processing fees checkbox once ticked.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-checkbox-border-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-coverProcessingFees-checkbox-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of the cover processing fees checkbox.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-checkbox-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_checkout-form-fields-input-border-radius)',
      },
    ],
    options: [{ value: '', label: 'Auto' }, ...THEME_OPTIONS.indicatorRadius],
    defaultValue: '',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.checkbox.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Tick',
    description: 'Color of the tick inside a ticked cover processing fees checkbox.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-checkbox-color',
        transform: 'direct',
        cssDefault: '#ffffff',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.switch.styles.size',
    cssProperty: 'size',
    type: 'size',
    label: 'Size',
    description: 'Height of the cover processing fees switch; its track is 1.75 times as wide.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-switch-size',
        transform: 'px',
        cssDefault: '20px',
      },
    ],
    options: 'indicatorSize' as OptionsRef,
    defaultValue: '',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.switch.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Track',
    description: 'Track color of the cover processing fees switch while it is off.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-switch-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_price-picker-option-indicator-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.switch.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Track on',
    description: 'Track color of the cover processing fees switch once it is on.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-switch-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_color-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.switch.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Knob',
    description: 'Color of the knob that slides along the cover processing fees switch.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-switch-color',
        transform: 'direct',
        cssDefault: 'var(--cp_price-picker-option-indicator-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.shape',
    type: 'select',
    label: 'Shape',
    description: 'Shape of the cover processing fees selected mark (dot, tick or star).',
    category: 'coverProcessingFees',
    cssVars: [],
    options: 'indicatorShape' as OptionsRef,
    defaultValue: 'tick',
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.size',
    cssProperty: 'size',
    type: 'size',
    label: 'Size',
    description: 'Size of the cover processing fees mark.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-indicator-size',
        transform: 'px',
        cssDefault: '20px',
      },
    ],
    options: 'indicatorSize' as OptionsRef,
    defaultValue: '',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of the cover processing fees mark (full = circle, none = square).',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-indicator-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_price-picker-option-indicator-border-radius)',
      },
    ],
    options: [{ value: '', label: 'Auto' }, ...THEME_OPTIONS.indicatorRadius],
    defaultValue: '',
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of the cover processing fees mark.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-indicator-border-width',
        transform: 'px',
        cssDefault: 'var(--cp_price-picker-option-indicator-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background of an unselected cover processing fees mark.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-indicator-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_price-picker-option-indicator-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Background of a selected cover processing fees mark.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-indicator-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_price-picker-option-indicator-background-color-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of an unselected cover processing fees mark.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-indicator-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_price-picker-option-indicator-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description: 'Border color of a selected cover processing fees mark.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-indicator-border-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_color-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.indicator.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Mark',
    description: 'Color of the dot, tick or star inside a selected cover processing fees mark.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-indicator-color',
        transform: 'direct',
        cssDefault: 'var(--cp_color-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.label.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Label',
    description: 'Color of the cover processing fees label.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-label-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-fields-label-color)',
      },
    ],
    resetable: true,
  },
  // No line-height defaults, matching Fields > Label: one is set only with a size.
  ...fontGroup(
    'variables.elements.checkout.form.coverProcessingFees.label.styles',
    'checkout-form-coverProcessingFees-label',
    'cover processing fees label',
    'coverProcessingFees',
    {
      fontFamily: 'var(--cp_checkout-form-fields-label-font-family)',
      fontStyle: 'var(--cp_checkout-form-fields-label-font-style)',
      fontWeight: 'var(--cp_checkout-form-fields-label-font-weight)',
      fontSize: 'var(--cp_checkout-form-fields-label-font-size)',
    },
  ),
  {
    path: 'variables.elements.checkout.form.coverProcessingFees.amount.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Amount',
    description: 'Color of the amount beside the cover processing fees label.',
    category: 'coverProcessingFees',
    cssVars: [
      {
        cssVar: 'checkout-form-coverProcessingFees-amount-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-coverProcessingFees-label-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.coverProcessingFees.amount.styles',
    'checkout-form-coverProcessingFees-amount',
    'cover processing fees amount',
    'coverProcessingFees',
    {
      fontFamily: 'var(--cp_checkout-form-coverProcessingFees-label-font-family)',
      fontStyle: 'var(--cp_checkout-form-coverProcessingFees-label-font-style)',
      fontWeight: 'var(--cp_checkout-form-coverProcessingFees-label-font-weight)',
      fontSize: 'var(--cp_checkout-form-coverProcessingFees-label-font-size)',
    },
  ),
];

// ============================================================================
// Order bump entries (cards under the variants)
// ============================================================================

const ORDER_BUMP_ENTRIES: ThemeProperty[] = [
  {
    // Bumps sit under the variants, and follow them between the columns unless
    // this is set. The path stays under `form` with the rest of the bump
    // styling — the node is named for where the cards started, not where they
    // can now be placed.
    path: 'variables.elements.checkout.form.orderBumps.position',
    type: 'select',
    label: 'Position',
    description: 'Where order bumps are displayed.',
    category: 'checkout',
    cssVars: [],
    options: [
      { value: 'form', label: 'Form' },
      { value: 'content', label: 'Content' },
    ],
  },
  // ---------------------------------------------------------------------------
  // Card arrangement. Read in JS (OrderBumps.js), not through CSS, so every
  // entry below carries no cssVars. Each default is what the card did before
  // these existed, so an untouched theme renders unchanged.
  // ---------------------------------------------------------------------------
  {
    path: 'variables.elements.checkout.form.orderBumps.layout',
    type: 'select',
    label: 'List or grid',
    description: 'Order bump cards stacked down the page, or side by side in a grid.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 'list',
    options: [
      { value: 'list', label: 'List' },
      { value: 'grid-2', label: '2 columns' },
      { value: 'grid-3', label: '3 columns' },
    ],
  },
  {
    // Every style keeps a real checkbox behind it — the card is a <label>, so an
    // actual <button> would be interactive content and swallow the click that
    // takes the bump. Radio and checkbox are both `mark`: the difference between
    // them is the mark's own shape and radius, below.
    path: 'variables.elements.checkout.form.orderBumps.acceptStyle',
    type: 'select',
    // Each pick has its own element under Elements — Switch, Mark or Button.
    // "Style" sat directly under a heading of the same name.
    label: 'Selector',
    description: 'What the buyer taps to accept the offer.',
    category: 'checkout',
    cssVars: [],
    // Unset is the Switch preset, the first chip in the design panel: a
    // fresh page and a discarded one must both light it.
    defaultValue: 'switch',
    options: [
      { value: 'switch', label: 'Switch' },
      { value: 'mark', label: 'Mark' },
      { value: 'button', label: 'Button' },
      { value: 'link', label: 'Link' },
      { value: 'none', label: 'None' },
    ],
  },
  {
    // Named for the Call to action element under Elements, which dresses the
    // row or bar this picks: the surface is the call to action's, whatever
    // control sits in it.
    path: 'variables.elements.checkout.form.orderBumps.acceptAppearance',
    type: 'select',
    label: 'Call to action layout',
    description:
      'What the call to action sits on: the offer itself, a boxed row of its own, or a bar flush with the sides of the card.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 'offer',
    options: [
      { value: 'bar', label: 'Bar' },
      { value: 'row', label: 'Row' },
      { value: 'offer', label: 'Beside the offer' },
    ],
  },
  {
    // One value, not an edge and a side: a seller picks a corner. Beside the
    // offer the control takes a column at one side, so the top centre means
    // nothing there; a row or bar lies above or below the offer, so the middle
    // edge means nothing on those. The editor hides each — see the dashboard's
    // themeFieldRegistry.
    path: 'variables.elements.checkout.form.orderBumps.acceptPosition',
    type: 'select',
    label: 'Call to action position',
    description: 'Where the call to action sits on the card.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 'top-right',
    options: [
      { value: 'top-right', label: 'Top right' },
      { value: 'top-left', label: 'Top left' },
      { value: 'top-center', label: 'Top center' },
      { value: 'center-right', label: 'Center right' },
      { value: 'center-left', label: 'Center left' },
      { value: 'bottom-right', label: 'Bottom right' },
      { value: 'bottom-left', label: 'Bottom left' },
      { value: 'bottom-center', label: 'Bottom center' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.width',
    type: 'select',
    label: 'Button width',
    description:
      'Whether the accept button fits its text or fills its line, sitting where the position puts it.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 'fit',
    options: [
      { value: 'fit', label: 'Fit to text' },
      { value: 'full', label: 'Full width' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.mediaPosition',
    type: 'select',
    label: 'Image position',
    description: 'Where the offer thumbnail sits on the card.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 'left',
    // No Hidden: a bump with no image renders none anyway, and a seller who
    // wants one gone takes it off the product. `none` is still HONOURED (see
    // getOrderBumpLayout) for the themes that already carry it.
    options: [
      { value: 'left', label: 'Left' },
      { value: 'right', label: 'Right' },
      { value: 'top', label: 'Top' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.horizontalAlign',
    type: 'select',
    label: 'Content alignment',
    description: 'Horizontal alignment of the card content.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 'left',
    options: [
      { value: 'left', label: 'Left' },
      { value: 'center', label: 'Center' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.descriptionPosition',
    type: 'select',
    label: 'Content width',
    description:
      'Whether the features, description and call out run the full card width or stay inset in the text column, clearing the image beside it.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 'column',
    options: [
      { value: 'column', label: 'Indented' },
      { value: 'full', label: 'Full width' },
    ],
  },
  {
    // Unset keeps what the card did before the field existed: beside the title
    // only when nothing opens the card ahead of it — see OrderBumpCardLayout.
    path: 'variables.elements.checkout.form.orderBumps.badge.position',
    type: 'select',
    label: 'Highlight position',
    description:
      'Whether the highlight sits on a line of its own above the title, or at the end of the title row, wrapping above it when the row is too narrow.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 'above',
    options: [
      { value: 'above', label: 'Above title' },
      { value: 'inline', label: 'Beside title' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of order bump cards.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of order bump cards.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of order bump cards.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-border-width',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.styles.borderStyle',
    cssProperty: 'borderStyle',
    type: 'select',
    label: 'Border style',
    description: 'Border style of order bump cards.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-border-style',
        transform: 'direct',
        cssDefault: 'solid',
      },
    ],
    options: 'borderStyle' as OptionsRef,
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of order bump cards.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-radius)',
      },
    ],
    options: 'sizePx' as OptionsRef,
    resetable: true,
  },
  // The amount box on a pay-what-you-want bump: a third per-context PWYW
  // subsection beside the price option's and the variant option's, chained to
  // the base PWYW input the same way.
  ...pwywInputGroup(
    'variables.elements.checkout.form.orderBumps.pwywInput',
    'checkout-form-orderBumps-pwywInput',
    { category: 'checkout', chainTo: 'pwyw-input' },
  ),
  ...pwywSuffixGroup(
    'variables.elements.checkout.form.orderBumps.pwywSuffix',
    'checkout-form-orderBumps-pwywSuffix',
    'checkout',
  ),
  {
    // Sets the thumbnail's box, which is what sizes the columns: the text column
    // takes whatever the image leaves, so this is the card's split control as
    // much as it is the image's.
    // Class-driven rather than a px variable: Full width is not a length, and a
    // `px` transform would emit `fullpx`.
    path: 'variables.elements.checkout.form.orderBumps.image.styles.size',
    type: 'select',
    label: 'Image size',
    description:
      'Size of the thumbnail on an order bump card. Full width spans the card, which moves the picture above the copy.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 64,
    options: [
      { value: 64, label: 'Small' },
      { value: 128, label: 'Medium' },
      { value: 192, label: 'Large' },
      { value: 'full', label: 'Full width' },
    ],
    // A full-width picture leaves no column to sit beside, so the card stacks
    // whatever the placement says — see getOrderBumpLayout. Writing the
    // placement keeps the Image position field from showing a Left that never
    // happens, and lets the Banner preset chip match a card built from the fields.
    setAlso: {
      full: { 'variables.elements.checkout.form.orderBumps.mediaPosition': 'top' },
    },
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.image.styles.aspectRatio',
    type: 'select',
    label: 'Image ratio',
    description:
      'Shape of the thumbnail on an order bump card. Original keeps the picture as it was uploaded.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 'square',
    options: [
      { value: 'square', label: 'Square' },
      { value: 'landscape', label: 'Landscape (4:3)' },
      { value: 'wide', label: 'Wide (16:9)' },
      { value: 'original', label: 'Original' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.image.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Image radius',
    description: 'Border radius of the thumbnail on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-image-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-radius)',
      },
    ],
    options: 'sizePx' as OptionsRef,
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Background color of a selected order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description: 'Border color of a selected order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-border-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color-selected)',
      },
    ],
    resetable: true,
  },
  // The highlight chains every token to the Price options highlight, so the
  // two pills match until a seller styles the bump's on its own.
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Highlight background',
    description: 'Background color of the highlight pill on an order bump.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-badge-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_price-picker-option-badge-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Highlight text',
    description: 'Text color of the highlight pill on an order bump.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-badge-color',
        transform: 'direct',
        cssDefault: 'var(--cp_price-picker-option-badge-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Highlight border',
    description: 'Border color of the highlight pill on an order bump.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-badge-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_price-picker-option-badge-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Highlight border width',
    description: 'Border width of the highlight pill on an order bump.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-badge-border-width',
        transform: 'px',
        cssDefault: 'var(--cp_price-picker-option-badge-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Highlight border radius',
    description: 'Border radius of the highlight pill on an order bump.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-badge-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_price-picker-option-badge-border-radius)',
      },
    ],
    options: 'badgeRadius' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.styles.padding',
    cssProperty: 'padding',
    type: 'size',
    label: 'Highlight padding',
    description: 'Padding inside the highlight pill on an order bump.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-badge-padding',
        transform: 'px',
        cssDefault: 'var(--cp_price-picker-option-badge-padding)',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.icon.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Highlight icon',
    description: 'Color of the icon leading the highlight on an order bump.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-badge-icon-color',
        transform: 'direct',
        // Unset, the icon takes the highlight's text colour.
        cssDefault: 'currentColor',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.badge.icon.styles.size',
    cssProperty: 'size',
    type: 'size',
    label: 'Highlight icon size',
    description: 'Size of the icon leading the highlight on an order bump.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-badge-icon-size',
        transform: 'px',
        // Unset, the icon follows the highlight's font size.
        cssDefault: '1em',
      },
    ],
    options: [
      { value: '', label: 'Auto' },
      { value: 10, label: '10px' },
      { value: 12, label: '12px' },
      { value: 14, label: '14px' },
      { value: 16, label: '16px' },
      { value: 18, label: '18px' },
      { value: 20, label: '20px' },
      { value: 24, label: '24px' },
    ],
  },
  ...fontGroup(
    'variables.elements.checkout.form.orderBumps.badge.styles',
    'checkout-form-orderBumps-badge',
    'order bump highlight',
    'checkout',
    {
      fontFamily: 'var(--cp_price-picker-option-badge-font-family)',
      fontStyle: 'var(--cp_price-picker-option-badge-font-style)',
      fontWeight: 'var(--cp_price-picker-option-badge-font-weight)',
      fontSize: 'var(--cp_price-picker-option-badge-font-size)',
      lineHeight: 'var(--cp_price-picker-option-badge-line-height)',
    },
  ),
  {
    path: 'variables.elements.checkout.form.orderBumps.callToAction.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Call to action',
    description: 'Color of the call-to-action line above an order bump title.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-callToAction-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-orderBumps-title-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.orderBumps.callToAction.styles',
    'checkout-form-orderBumps-callToAction',
    'order bump call to action',
    'checkout',
    // Semibold body copy is what the line was before it had fields of its own.
    { ...FONT_BODY, fontWeight: '600' },
  ),
  {
    path: 'variables.elements.checkout.form.orderBumps.shortDescription.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Short description',
    description: 'Color of the single-line description under an order bump title.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-shortDescription-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-description-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.orderBumps.shortDescription.styles',
    'checkout-form-orderBumps-shortDescription',
    'order bump short description',
    'checkout',
    {
      fontFamily: 'var(--cp_card-description-font-family)',
      fontStyle: 'var(--cp_card-description-font-style)',
      fontWeight: 'var(--cp_card-description-font-weight)',
      fontSize: 'var(--cp_card-description-font-size)',
      lineHeight: 'var(--cp_font-body-line-height)',
    },
  ),
  {
    path: 'variables.elements.checkout.form.orderBumps.discountedFromPrice.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Discounted from',
    description: 'Color of the struck-through original price on an order bump.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-discounted-from-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-discountedFromPrice-color)',
      },
    ],
    resetable: true,
  },
  // A bump's price lines follow the Cards element until set, the way variant
  // options and ticket types do — not the price picker's option lines.
  ...fontGroup(
    'variables.elements.checkout.form.orderBumps.discountedFromPrice.styles',
    'checkout-form-orderBumps-discounted-from',
    'order bump discounted from price',
    'checkout',
    {
      fontFamily: 'var(--cp_card-discountedFromPrice-font-family)',
      fontStyle: 'var(--cp_card-discountedFromPrice-font-style)',
      fontWeight: 'var(--cp_card-discountedFromPrice-font-weight)',
      fontSize: 'var(--cp_card-discountedFromPrice-font-size)',
    },
  ),
  {
    path: 'variables.elements.checkout.form.orderBumps.setupFee.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Setup fee',
    description: 'Color of the setup fee line on an order bump.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-setup-fee-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-detail-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.orderBumps.setupFee.styles',
    'checkout-form-orderBumps-setup-fee',
    'order bump setup fee',
    'checkout',
    FONT_BODY,
  ),
  // Three controls, three elements. The mark keeps the price picker's field
  // set and reads its resting values from the picker, so the two marks on a
  // page agree until a seller separates them. The switch and the button have
  // their own paths below: sharing the mark's meant a full-radius mark became
  // a pill button, and a switch knob painted the mark's fill.
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Selector',
    description: 'Color of the dot, tick or star inside a selected order bump mark.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-indicator-color',
        transform: 'direct',
        // The main colour, as the accept button already is — not the price
        // picker's mark, which themes tend to paint in the text colour.
        cssDefault: 'var(--cp_color-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of an unselected order bump mark.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-indicator-border-color',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description: 'Border color of a selected order bump mark.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-indicator-border-color-selected',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  // Mark geometry, at parity with a price option's — a radio is a dot at full
  // radius, a checkbox a tick at a square one, so the pair below is what tells
  // them apart. The box fields rest on the price picker's mark in checkout.css,
  // which is why they have no default here.
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.shape',
    type: 'select',
    label: 'Shape',
    description: 'Shape of the order bump selected mark (dot, tick or star).',
    category: 'checkout',
    cssVars: [],
    options: 'indicatorShape' as OptionsRef,
    defaultValue: 'dot',
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of the order bump mark (full = circle, none = square).',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-indicator-border-radius',
        transform: 'px',
      },
    ],
    // Unset follows the price picker's mark in checkout.css, so the field
    // needs an Auto to read: the picker's own scale starts at None, and the
    // select showed that over a round mark.
    options: [{ value: '', label: 'Auto' }, ...THEME_OPTIONS.indicatorRadius],
    defaultValue: '',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.size',
    cssProperty: 'size',
    type: 'size',
    label: 'Size',
    description: 'Size of the order bump mark.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-indicator-size',
        transform: 'px',
        // A bump's card is bigger than a price option's row and its selector is
        // the one control on it, so it does not follow the picker's 16px.
        cssDefault: '20px',
      },
    ],
    options: 'indicatorSize' as OptionsRef,
    defaultValue: '',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of the order bump mark.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-indicator-border-width',
        transform: 'px',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background of an unselected order bump mark.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-indicator-background-color',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.indicator.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Background of a selected order bump mark.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-indicator-background-color-selected',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  // The switch: a track that changes colour with the bump, and a knob that
  // slides along it. Its own three colours and size — see the mark above.
  {
    path: 'variables.elements.checkout.form.orderBumps.switch.styles.size',
    cssProperty: 'size',
    type: 'size',
    label: 'Size',
    description: 'Height of the order bump switch; its track is 1.75 times as wide.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-switch-size',
        transform: 'px',
        cssDefault: '20px',
      },
    ],
    options: 'indicatorSize' as OptionsRef,
    defaultValue: '',
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.switch.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Track',
    description: 'Track color of the order bump switch when the bump is not in the order.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-switch-background-color',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.switch.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Track on',
    description: 'Track color of the order bump switch once the bump is in the order.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-switch-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_color-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.switch.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Knob',
    description: 'Color of the knob that slides along the order bump switch.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-switch-color',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  // The accept button: the call to action recast as the control. Its copy and
  // font are the call to action's; the box, and the colour of the copy on it,
  // are its own. Nothing here has a cssDefault: checkout.css reads each unset
  // value from the submit button's variables (the ones Button.js reads), so
  // the bump button is the checkout's button in miniature, hover included,
  // until a seller paints it. Its text follows the same path; only a
  // background of its own gets a text derived against it — see CheckoutCSS.js.
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.fill',
    type: 'select',
    label: 'Fill',
    description:
      'Whether the order bump accept button is filled in the Button color or drawn as an outline in it.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 'solid',
    options: [
      { value: 'solid', label: 'Solid' },
      { value: 'outline', label: 'Outline' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text',
    description:
      'Text color of the order bump accept button. Unset, a solid button follows the Button element, or takes black or white on a background of its own, and an outline button follows its border.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptButton-color',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.selectedStyles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Selected text',
    description:
      'Text color of the order bump accept button once the bump is in the order. Unset, it follows the Button element on hover.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptButton-color-selected',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description:
      'Background of the order bump accept button. Unset, it follows the Button element.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptButton-background-color',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description:
      'Background of the order bump accept button once the bump is in the order. Unset, it follows the Button element on hover.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptButton-background-color-selected',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description:
      'Border color of the order bump accept button. Unset, it follows the Button element.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptButton-border-color',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description:
      'Border color of the order bump accept button once the bump is in the order. Unset, it follows the Button element on hover.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptButton-border-color-selected',
        transform: 'direct',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description:
      'Border width of the order bump accept button. Unset, it follows the Button element.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptButton-border-width',
        transform: 'px',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description:
      'Border radius of the order bump accept button. Unset, it follows the Button element.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptButton-border-radius',
        transform: 'px',
      },
    ],
    options: 'sizePx' as OptionsRef,
  },
  // Two paddings, as the input fields have: the button fits its text unless
  // widened, so its sides are its own to set apart from its height.
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.paddingX',
    cssProperty: 'paddingX',
    type: 'size',
    label: 'Horizontal padding',
    description: 'Horizontal padding of the order bump accept button.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptButton-padding-x',
        transform: 'px',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptButton.styles.paddingY',
    cssProperty: 'paddingY',
    type: 'size',
    label: 'Vertical padding',
    description: 'Vertical padding of the order bump accept button.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptButton-padding-y',
        transform: 'px',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  // The link: the call to action recast as a line of underlined copy. Unset,
  // every value reads the Call to action element's variable, so a link is that
  // copy underlined until a seller dresses it apart.
  {
    path: 'variables.elements.checkout.form.orderBumps.link.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text',
    description:
      'Text color of the order bump accept link. Unset, it follows the Call to action element.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-link-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-orderBumps-callToAction-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.link.selectedStyles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Selected text',
    description:
      'Text color of the order bump accept link once the bump is in the order. Unset, a shade darker than the resting text color.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-link-color-selected',
        transform: 'direct',
        // Mixed in CSS, not derived in CheckoutCSS.js: the resting colour is
        // usually an inherited variable chain the JS never resolves. The
        // dashboard swatch mixes the same 10% black (themeDefaults.js).
        cssDefault: 'color-mix(in srgb, var(--cp_checkout-form-orderBumps-link-color), black 10%)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.link.styles.textDecoration',
    cssProperty: 'textDecoration',
    type: 'select',
    label: 'Underline',
    description: 'Whether the order bump accept link is underlined.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-link-text-decoration',
        transform: 'direct',
        cssDefault: 'underline',
      },
    ],
    defaultValue: 'underline',
    options: [
      { value: 'underline', label: 'Underline' },
      { value: 'none', label: 'None' },
    ],
  },
  ...fontGroup(
    'variables.elements.checkout.form.orderBumps.link.styles',
    'checkout-form-orderBumps-link',
    'order bump link',
    'checkout',
    {
      fontFamily: 'var(--cp_checkout-form-orderBumps-callToAction-font-family)',
      fontStyle: 'var(--cp_checkout-form-orderBumps-callToAction-font-style)',
      fontWeight: 'var(--cp_checkout-form-orderBumps-callToAction-font-weight)',
      fontSize: 'var(--cp_checkout-form-orderBumps-callToAction-font-size)',
      lineHeight: 'var(--cp_checkout-form-orderBumps-callToAction-line-height)',
    },
  ),
  // The seller's line above a card ("Special one time offer") is the Cards
  // group name in every respect, as a variant group's name is: colour, type
  // and the padding to the card all chain there until set apart.
  {
    path: 'variables.elements.checkout.form.orderBumps.heading.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Heading',
    description: 'Color of the heading above an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-heading-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-groupName-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.heading.styles.paddingBottom',
    cssProperty: 'paddingBottom',
    type: 'size',
    label: 'Padding bottom',
    description: 'Space between the heading and the order bump card beneath it.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-heading-padding-bottom',
        transform: 'px',
        cssDefault: 'var(--cp_card-groupName-padding-bottom)',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  ...fontGroup(
    'variables.elements.checkout.form.orderBumps.heading.styles',
    'checkout-form-orderBumps-heading',
    'order bump heading',
    'checkout',
    {
      fontFamily: 'var(--cp_card-groupName-font-family)',
      fontStyle: 'var(--cp_card-groupName-font-style)',
      fontWeight: 'var(--cp_card-groupName-font-weight)',
      fontSize: 'var(--cp_card-groupName-font-size)',
      lineHeight: 'var(--cp_font-body-line-height)',
    },
  ),
  // The "what you get" list above the description. It rests on the Title's
  // colour in the body font a step heavier, so it stands apart from the
  // description with nothing set; the icons follow the text until painted.
  {
    path: 'variables.elements.checkout.form.orderBumps.features.layout',
    type: 'select',
    label: 'Layout',
    description:
      'Whether the features run down the card as a list or across it in two columns, dropping to one where the card is narrow.',
    category: 'checkout',
    cssVars: [],
    defaultValue: 'list',
    options: [
      { value: 'list', label: 'List' },
      { value: 'grid', label: 'Grid' },
    ],
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.features.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Features',
    description: 'Text color of the features list on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-features-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-orderBumps-title-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.features.styles.gap',
    cssProperty: 'gap',
    type: 'size',
    label: 'Spacing',
    description: 'Space between the lines of the features list on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-features-gap',
        transform: 'px',
        cssDefault: '0.375rem',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.features.icon.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Feature icon',
    description: 'Color of the icon leading each line of the features list on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-features-icon-color',
        transform: 'direct',
        // Unset, the icon takes the line's text colour.
        cssDefault: 'currentColor',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.features.icon.styles.size',
    cssProperty: 'size',
    type: 'size',
    label: 'Feature icon size',
    description: 'Size of the icon leading each line of the features list on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-features-icon-size',
        transform: 'px',
        // Unset, the icon follows the line's font size.
        cssDefault: '1.25em',
      },
    ],
    options: [
      { value: '', label: 'Auto' },
      { value: 10, label: '10px' },
      { value: 12, label: '12px' },
      { value: 14, label: '14px' },
      { value: 16, label: '16px' },
      { value: 18, label: '18px' },
      { value: 20, label: '20px' },
      { value: 24, label: '24px' },
    ],
  },
  ...fontGroup(
    'variables.elements.checkout.form.orderBumps.features.styles',
    'checkout-form-orderBumps-features',
    'order bump features',
    'checkout',
    { ...FONT_BODY, fontWeight: '500' },
  ),
  // The call out box under the description. Its text rests on the Title, its
  // background is a wash of that text and its border the card's, so it reads
  // on any theme with nothing set; the copy is the card description's type.
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Call out',
    description: 'Text color of the call out box on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-callout-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-orderBumps-title-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Call out background',
    description: 'Background color of the call out box on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-callout-background-color',
        transform: 'direct',
        cssDefault:
          'color-mix(in srgb, var(--cp_checkout-form-orderBumps-callout-color) 4%, transparent)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Call out border',
    description: 'Border color of the call out box on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-callout-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-orderBumps-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Call out border width',
    description: 'Border width of the call out box on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-callout-border-width',
        transform: 'px',
        cssDefault: '1px',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Call out border radius',
    description: 'Border radius of the call out box on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-callout-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_checkout-border-radius)',
      },
    ],
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.styles.padding',
    cssProperty: 'padding',
    type: 'size',
    label: 'Call out padding',
    description: 'Padding inside the call out box on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-callout-padding',
        transform: 'px',
        cssDefault: '0.75rem',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.icon.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Call out icon',
    description: 'Color of the icon leading the call out box on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-callout-icon-color',
        transform: 'direct',
        // Unset, the icon takes the box's text colour.
        cssDefault: 'currentColor',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.callout.icon.styles.size',
    cssProperty: 'size',
    type: 'size',
    label: 'Call out icon size',
    description: 'Size of the icon leading the call out box on an order bump card.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-callout-icon-size',
        transform: 'px',
        // Unset, the icon follows the box's font size.
        cssDefault: '1.25em',
      },
    ],
    options: [
      { value: '', label: 'Auto' },
      { value: 10, label: '10px' },
      { value: 12, label: '12px' },
      { value: 14, label: '14px' },
      { value: 16, label: '16px' },
      { value: 18, label: '18px' },
      { value: 20, label: '20px' },
      { value: 24, label: '24px' },
    ],
  },
  ...fontGroup(
    'variables.elements.checkout.form.orderBumps.callout.styles',
    'checkout-form-orderBumps-callout',
    'order bump call out',
    'checkout',
    {
      fontFamily: 'var(--cp_card-description-font-family)',
      fontStyle: 'var(--cp_card-description-font-style)',
      fontWeight: 'var(--cp_card-description-font-weight)',
      fontSize: 'var(--cp_card-description-font-size)',
      lineHeight: 'var(--cp_font-body-line-height)',
    },
  ),
  // The card's own lines chain to the Cards element the way a variant option's
  // do, so Cards > Name and Description restyle every card section at once.
  {
    path: 'variables.elements.checkout.form.orderBumps.title.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Title',
    description: 'Color of the order bump section heading and card titles.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-title-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-name-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.orderBumps.title.styles',
    'checkout-form-orderBumps-title',
    'order bump title',
    'checkout',
    {
      fontFamily: 'var(--cp_card-name-font-family)',
      fontStyle: 'var(--cp_card-name-font-style)',
      fontWeight: 'var(--cp_card-name-font-weight)',
      fontSize: 'var(--cp_card-name-font-size)',
      lineHeight: 'var(--cp_font-body-line-height)',
    },
  ),
  {
    path: 'variables.elements.checkout.form.orderBumps.price.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Price',
    description: 'Color of the order bump card price.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-price-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-price-color)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.orderBumps.price.styles',
    'checkout-form-orderBumps-price',
    'order bump price',
    'checkout',
    {
      fontFamily: 'var(--cp_card-price-font-family)',
      fontStyle: 'var(--cp_card-price-font-style)',
      fontWeight: 'var(--cp_card-price-font-weight)',
      fontSize: 'var(--cp_card-price-font-size)',
    },
  ),
  // ---------------------------------------------------------------------------
  // The call to action's own surface, on the appearances that give it one (a
  // row, a bar). It carries no text color: the copy on it is the call to
  // action, which already has one.
  // ---------------------------------------------------------------------------
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of the order bump accept row.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptBar-background-color',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
    resetable: true,
  },
  {
    // Behind it is the resting background, so a theme that never set one has a
    // line that does not change colour when the bump is taken.
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Background color of the order bump accept row once the bump is selected.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptBar-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-orderBumps-acceptBar-background-color)',
      },
    ],
    resetable: true,
  },
  {
    // The bar's rule and box follow the CARD's selected border, not their own
    // resting colour: a taken card's edge changes colour, and a divider left
    // behind in the resting shade reads as a stale line across it.
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border color',
    description: 'Border color of the order bump accept row once the bump is selected.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptBar-border-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-orderBumps-border-color-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border color',
    description: 'Border color of the order bump accept row.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptBar-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_checkout-form-orderBumps-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Border width',
    description: 'Border width of the order bump accept row.',
    category: 'checkout',
    cssVars: [
      {
        // No default here: a row that owns its line comes boxed and one beside
        // the offer does not, and a :root default is the same in both. The
        // fallback each arrangement uses is in checkout.css.
        cssVar: 'checkout-form-orderBumps-acceptBar-border-width',
        transform: 'px',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of the order bump accept row.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptBar-border-radius',
        transform: 'px',
      },
    ],
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.padding',
    cssProperty: 'padding',
    type: 'size',
    label: 'Padding',
    description: 'Inner padding of the order bump accept row.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptBar-padding',
        transform: 'px',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  {
    // The hairline between the accept line and the offer, drawn on the side
    // facing it — separate from the border so a bar can have a rule under it
    // without being boxed in. No :root default: a bar comes with one and a row
    // does not, and the fallback each uses is in checkout.css.
    path: 'variables.elements.checkout.form.orderBumps.acceptBar.styles.dividerWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Divider',
    description: 'Width of the rule between the order bump accept line and the offer.',
    category: 'checkout',
    cssVars: [
      {
        cssVar: 'checkout-form-orderBumps-acceptBar-divider-width',
        transform: 'px',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
];

// ============================================================================
// Trust entries (secure payments, or pay with card, powered by stripe)
// ============================================================================

const TRUST_ENTRIES: ThemeProperty[] = [
  {
    path: 'variables.elements.checkout.form.orPayWithCard.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text',
    description: 'Text color of the "or pay with card" divider.',
    category: 'trust',
    cssVars: [
      {
        cssVar: 'checkout-form-orPayWithCard-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-tertiary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.orPayWithCard.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Border',
    description: 'Border color of the "or pay with card" divider.',
    category: 'trust',
    cssVars: [
      {
        cssVar: 'checkout-form-orPayWithCard-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_border-subtle)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.form.securePayments.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Background',
    description: 'Background color of the secure payments badge.',
    category: 'trust',
    cssVars: [
      {
        cssVar: 'checkout-form-securePayments-background-color',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
  },
  {
    path: 'variables.elements.checkout.form.securePayments.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text',
    description: 'Text color of the secure payments badge.',
    category: 'trust',
    cssVars: [
      {
        cssVar: 'checkout-form-securePayments-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-tertiary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.checkout.form.securePayments.styles',
    'checkout-form-securePayments',
    'secure payments badge',
    'trust',
    { ...FONT_BODY, fontSize: 'var(--cp_font-body-size-sm)', lineHeight: undefined },
  ),
  {
    path: 'variables.elements.poweredByStripe.styles.badgeStyle',
    type: 'select',
    label: 'Badge style',
    description: 'Style variant of the Powered by Stripe badge.',
    category: 'trust',
    cssVars: [],
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'outline_black', label: 'Black' },
      { value: 'outline_white', label: 'White' },
    ],
  },
];

// ============================================================================
// Confirmation entries
// ============================================================================

const CONFIRMATION_ENTRIES: ThemeProperty[] = [
  // --- Mark ---
  {
    path: 'variables.elements.checkout.confirmation.mark.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Mark',
    description: 'Color of the confirmation check icon.',
    category: 'confirmation',
    cssVars: [
      {
        cssVar: 'checkout-confirmation-mark-color',
        transform: 'direct',
        cssDefault: 'var(--cp_color-primary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.checkout.confirmation.mark.styles.size',
    cssProperty: 'size',
    type: 'size',
    label: 'Mark size',
    description: 'Size of the confirmation check icon.',
    category: 'confirmation',
    cssVars: [
      {
        cssVar: 'checkout-confirmation-mark-size',
        transform: 'px',
      },
    ],
    options: 'confirmationMarkSize' as OptionsRef,
    defaultValue: '',
    resetable: true,
  },
  // --- Heading ---
  {
    path: 'variables.elements.checkout.confirmation.heading.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Heading',
    description: 'Color of the confirmation heading text.',
    category: 'confirmation',
    cssVars: [
      {
        cssVar: 'checkout-confirmation-heading-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  // --- Text ---
  {
    path: 'variables.elements.checkout.confirmation.text.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Text',
    description: 'Color of the confirmation message text.',
    category: 'confirmation',
    cssVars: [
      {
        cssVar: 'checkout-confirmation-text-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
];

// ============================================================================
// PWYW entries — base "Pay what you want" Label + Input elements
// (variables.elements.checkout.pwyw.*). These drive all three pay-what-you-want
// inputs (single-price, multi-price option, product variants) by default: the
// single-price input/heading consumes these vars directly (see
// PayWhatYouWantField.js), while the multi-price and variant pwywInput/pwywLabel
// groups chain their own defaults to these base vars (see pwywInputGroup /
// pwywLabelGroup `chainTo`). Base INPUT defaults chain to Fields > Input, so
// unstyled pages stay pixel-identical until a base or subsection override is
// set. Base LABEL entries have NO cssDefault at all (see pwywLabelGroup) — the
// single-price heading's own inline fallback (PayWhatYouWantField.js) handles
// the Fields > Label look, and the price-picker option's pwywLabel group (see
// PRICE_PICKER_ENTRIES) falls back to its OWN Cards-derived look instead of
// Fields, reconciling "PWYW label controls restyle it" with "unstyled PWYW
// option labels look like Cards".
// ============================================================================

// The PWYW input suffix ("Interval / Currency" — the cp_paywhatyouwant-currency
// span). Color + font only; NO cssDefault — the base -> subsection -> input
// chain lives in checkout.css nested var() so each context defaults to its own
// PWYW input placeholder/font.
function pwywSuffixGroup(
  basePath: string,
  prefix: string,
  cat: ThemePropertyCategory,
): ThemeProperty[] {
  return [
    {
      path: `${basePath}.styles.color`,
      cssProperty: 'color',
      type: 'color',
      label: 'Interval / Currency',
      description: 'Color of the pay-what-you-want input suffix (interval or currency code).',
      category: cat,
      cssVars: [{ cssVar: `${prefix}-color`, transform: 'direct' }],
      resetable: true,
    },
    ...fontGroup(`${basePath}.styles`, prefix, 'pay-what-you-want suffix', cat),
  ];
}

const PWYW_ENTRIES: ThemeProperty[] = [
  ...pwywLabelGroup('variables.elements.checkout.pwyw.label', 'pwyw-label', { category: 'pwyw' }),
  ...pwywSuffixGroup('variables.elements.checkout.pwyw.suffix', 'pwyw-suffix', 'pwyw'),
  // paddingY carries a `-set` mirror so the variant PWYW input (whose default
  // is the compact 0.375rem, not the base chain) can detect an explicit base
  // Vertical padding and follow it — see the variant pwywInputGroup call.
  ...pwywInputGroup('variables.elements.checkout.pwyw.input', 'pwyw-input', {
    category: 'pwyw',
  }).map(p =>
    p.path === 'variables.elements.checkout.pwyw.input.styles.paddingY'
      ? withSetMirrors([p])[0]
      : p,
  ),
];

// ============================================================================
// Toggle entries (enabled flags from group-color and heading toggleable)
// ============================================================================

const TOGGLE_ENTRIES: ThemeProperty[] = [
  // Product
  tog('variables.elements.product.logo.enabled', 'logo', 'product'),
  tog('variables.elements.product.title.enabled', 'product title', 'product'),
  tog('variables.elements.product.pricing.enabled', 'pricing', 'product'),
  tog('variables.elements.product.pricing.price.enabled', 'price', 'product'),
  tog('variables.elements.product.pricing.interval.enabled', 'interval', 'product'),
  tog(
    'variables.elements.product.pricing.discountedFromPrice.enabled',
    'discounted from price',
    'product',
  ),
  tog('variables.elements.product.pricing.setupFee.enabled', 'setup fee', 'product'),
  tog('variables.elements.product.pricing.trialPeriod.enabled', 'trial period', 'product'),
  tog('variables.elements.product.pricing.startDate.enabled', 'start date', 'product'),
  tog('variables.elements.product.pricing.planIterations.enabled', 'number of payments', 'product'),
  tog('variables.elements.product.pricing.prorationRenews.enabled', 'proration renews', 'product'),
  tog('variables.elements.product.pricing.prorationAmount.enabled', 'proration amount', 'product'),
  tog('variables.elements.product.pricing.badge.enabled', 'price highlight', 'product'),
  tog('variables.elements.product.pricePicker.enabled', 'price options', 'product'),
  tog('variables.elements.product.pricePicker.option.label.enabled', 'price label', 'product'),
  tog(
    'variables.elements.product.pricePicker.option.description.enabled',
    'price description',
    'product',
  ),
  tog('variables.elements.product.pricePicker.option.badge.enabled', 'price highlight', 'product'),
  tog('variables.elements.product.pricePicker.option.indicator.enabled', 'radio', 'product'),
  // Price-options per-line + structural visibility toggles (the picker's own
  // eyes, independent of the single-price block's pricing.* toggles).
  tog('variables.elements.product.pricePicker.tabs.enabled', 'price option tabs', 'product'),
  tog('variables.elements.product.pricePicker.title.enabled', 'price options heading', 'product'),
  tog(
    'variables.elements.product.pricePicker.description.enabled',
    'price options description',
    'product',
  ),
  tog('variables.elements.product.pricePicker.option.enabled', 'price option card', 'product'),
  tog(
    'variables.elements.product.pricePicker.option.price.enabled',
    'price option price',
    'product',
  ),
  tog(
    'variables.elements.product.pricePicker.option.interval.enabled',
    'price option interval',
    'product',
  ),
  tog(
    'variables.elements.product.pricePicker.option.discountedFromPrice.enabled',
    'price option compare-at price',
    'product',
  ),
  tog(
    'variables.elements.product.pricePicker.option.setupFee.enabled',
    'price option setup fee',
    'product',
  ),
  tog(
    'variables.elements.product.pricePicker.option.trialPeriod.enabled',
    'price option trial period',
    'product',
  ),
  tog(
    'variables.elements.product.pricePicker.option.startDate.enabled',
    'price option start date',
    'product',
  ),
  tog(
    'variables.elements.product.pricePicker.option.planIterations.enabled',
    'price option number of payments',
    'product',
  ),
  tog(
    'variables.elements.product.pricePicker.option.prorationRenews.enabled',
    'price option proration renews',
    'product',
  ),
  tog(
    'variables.elements.product.pricePicker.option.prorationAmount.enabled',
    'price option proration amount',
    'product',
  ),
  tog('variables.elements.product.event.date.enabled', 'event date', 'product'),
  tog('variables.elements.product.event.location.enabled', 'event location', 'product'),
  tog('variables.elements.product.event.virtual.enabled', 'virtual event', 'product'),
  // Checkout
  tog('variables.elements.checkout.divider.enabled', 'divider', 'checkout'),
  tog('variables.elements.checkout.form.orderBumps.enabled', 'order bumps', 'checkout'),
  // Per-part eyes on the bump card, each read by the matching StyledElement in
  // OrderBumps.js.
  tog('variables.elements.checkout.form.orderBumps.heading.enabled', 'bump heading', 'checkout'),
  tog('variables.elements.checkout.form.orderBumps.features.enabled', 'bump features', 'checkout'),
  tog('variables.elements.checkout.form.orderBumps.callout.enabled', 'bump call out', 'checkout'),
  tog('variables.elements.checkout.form.orderBumps.badge.enabled', 'bump highlight', 'checkout'),
  tog(
    'variables.elements.checkout.form.orderBumps.callToAction.enabled',
    'bump call to action',
    'checkout',
  ),
  tog('variables.elements.checkout.form.orderBumps.title.enabled', 'bump title', 'checkout'),
  tog(
    'variables.elements.checkout.form.orderBumps.shortDescription.enabled',
    'bump short description',
    'checkout',
  ),
  tog('variables.elements.checkout.form.orderBumps.price.enabled', 'bump price', 'checkout'),
  tog(
    'variables.elements.checkout.form.orderBumps.discountedFromPrice.enabled',
    'bump discounted from price',
    'checkout',
  ),
  tog('variables.elements.checkout.form.orderBumps.setupFee.enabled', 'bump setup fee', 'checkout'),
  tog('variables.elements.checkout.form.orderBumps.image.enabled', 'bump image', 'checkout'),
  tog(
    'variables.elements.checkout.form.orderBumps.pwywSuffix.enabled',
    'bump pwyw interval / currency',
    'checkout',
  ),
  // Fields
  tog('variables.elements.checkout.form.fields.label.enabled', 'field labels', 'fields'),
  tog('variables.elements.checkout.form.heading.enabled', 'form headings', 'fields'),
  tog('variables.elements.checkout.form.subheading.enabled', 'form subheadings', 'fields'),
  tog('variables.elements.checkout.form.divider.enabled', 'form dividers', 'fields'),
  tog(
    'variables.elements.checkout.form.fields.description.enabled',
    'field descriptions',
    'fields',
  ),
  // PWYW
  tog('variables.elements.checkout.form.pwyw.enabled', 'pay what you want', 'fields'),
  tog('variables.elements.checkout.form.pwyw.minPrice.enabled', 'PWYW minimum price', 'fields'),
  tog('variables.elements.checkout.pwyw.label.enabled', 'pay-what-you-want label', 'pwyw'),
  tog(
    'variables.elements.checkout.pwyw.suffix.enabled',
    'pay-what-you-want interval / currency',
    'pwyw',
  ),
  tog(
    'variables.elements.product.pricePicker.option.pwywSuffix.enabled',
    'price option pwyw interval / currency',
    'product',
  ),
  tog(
    'variables.elements.checkout.variants.variantOptions.pwywSuffix.enabled',
    'variant pwyw interval / currency',
    'variants',
  ),
  {
    path: 'variables.elements.checkout.pwyw.position',
    type: 'select',
    label: 'Position',
    description: 'Where the single-price pay-what-you-want input is displayed.',
    category: 'pwyw',
    cssVars: [],
    options: [
      { value: 'content-top', label: 'Content top' },
      { value: 'content-bottom', label: 'Content bottom' },
      { value: 'form-top', label: 'Form top' },
      { value: 'form-bottom', label: 'Form bottom' },
    ],
  },
  // Button
  tog('variables.elements.checkout.form.button.priceOnButton.enabled', 'price on button', 'button'),
  // Payments
  tog(
    'variables.elements.checkout.form.manualPayments.name.enabled',
    'manual payment name',
    'payments',
  ),
  tog(
    'variables.elements.checkout.form.manualPayments.description.enabled',
    'manual payment description',
    'payments',
  ),
  tog(
    'variables.elements.checkout.form.manualPayments.instructions.enabled',
    'manual payment instructions',
    'payments',
  ),
  // Price breakdown
  tog(
    'variables.elements.checkout.form.priceBreakdown.enabled',
    'price breakdown',
    'priceBreakdown',
  ),
  tog(
    'variables.elements.checkout.form.priceBreakdown.subtotal.enabled',
    'subtotal',
    'priceBreakdown',
  ),
  tog(
    'variables.elements.checkout.form.priceBreakdown.setupFee.enabled',
    'setup fee',
    'priceBreakdown',
  ),
  tog(
    'variables.elements.checkout.form.priceBreakdown.bulkDiscount.enabled',
    'bulk discount',
    'priceBreakdown',
  ),
  tog(
    'variables.elements.checkout.form.priceBreakdown.couponDiscount.enabled',
    'coupon discount',
    'priceBreakdown',
  ),
  tog(
    'variables.elements.checkout.form.priceBreakdown.orderBump.enabled',
    'order bump lines',
    'priceBreakdown',
  ),
  tog(
    'variables.elements.checkout.form.priceBreakdown.ticket.enabled',
    'ticket lines',
    'priceBreakdown',
  ),
  tog(
    'variables.elements.checkout.form.priceBreakdown.ticketFee.enabled',
    'booking fee lines',
    'priceBreakdown',
  ),

  tog('variables.elements.checkout.form.priceBreakdown.tax.enabled', 'tax', 'priceBreakdown'),
  tog(
    'variables.elements.checkout.form.priceBreakdown.startDate.enabled',
    'start date',
    'priceBreakdown',
  ),
  tog('variables.elements.checkout.form.priceBreakdown.trial.enabled', 'trial', 'priceBreakdown'),
  tog('variables.elements.checkout.form.priceBreakdown.fees.enabled', 'fees', 'priceBreakdown'),
  tog('variables.elements.checkout.form.priceBreakdown.total.enabled', 'total', 'priceBreakdown'),
  tog(
    'variables.elements.checkout.form.priceBreakdown.dueNow.enabled',
    'due now',
    'priceBreakdown',
  ),
  tog(
    'variables.elements.checkout.form.priceBreakdown.proration.enabled',
    'proration',
    'priceBreakdown',
  ),
  // Trust
  tog('variables.elements.checkout.form.securePayments.enabled', 'secure payments', 'trust'),
  tog('variables.elements.checkout.form.orPayWithCard.enabled', '"or pay with card"', 'trust'),
  tog('variables.elements.poweredByStripe.enabled', 'Powered by Stripe', 'trust'),
  // Our branding badge. NO defaultValue on purpose: an unset per-page value inherits
  // the seller-level global default (`showCheckoutPageBranding`) — see resolveBadge.
  // Free/trial sellers always show this and can't turn it off, and it's mutually
  // exclusive with the Stripe badge above (resolveBadge enforces both).
  {
    path: 'variables.elements.poweredByCheckoutPage.enabled',
    type: 'toggle',
    label: 'Show Powered by Checkout Page',
    description: 'Whether the Powered by Checkout Page badge is shown on the checkout.',
    category: 'trust',
    cssVars: [],
  },
];

// ============================================================================
// Non-style entries (custom CSS, card field)
// ============================================================================

const NON_STYLE_ENTRIES: ThemeProperty[] = [
  {
    path: 'variables.elements.checkout.form.stripeCardElement.input.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Card field',
    description: 'Text color of the Stripe card input field.',
    category: 'fields',
    cssVars: [{ cssVar: 'card-field', transform: 'direct' }],
  },
  {
    path: 'variables.customCss',
    type: 'text',
    label: 'Custom CSS',
    description: 'Custom CSS to inject into the checkout.',
    category: 'checkout',
    cssVars: [],
    defaultValue: '',
  },
];

// ============================================================================
// Label entries
// ============================================================================

const LABEL_ENTRIES: ThemeProperty[] = [
  // Product labels
  {
    path: 'variables.labels.soldByText',
    type: 'text',
    label: 'Sold by',
    description: 'Label for the "sold by" credit.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Sold by',
  },
  {
    path: 'variables.labels.intervalCountText',
    type: 'text',
    label: 'Interval count',
    description: 'Prefix for multi-interval subscriptions (e.g. "every 3 months").',
    category: 'labels',
    cssVars: [],
    defaultValue: 'every',
  },
  {
    path: 'variables.labels.dayText',
    type: 'text',
    label: 'Day',
    description: 'Label for the "day" interval.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'day',
  },
  {
    path: 'variables.labels.weekText',
    type: 'text',
    label: 'Week',
    description: 'Label for the "week" interval.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'week',
  },
  {
    path: 'variables.labels.monthText',
    type: 'text',
    label: 'Month',
    description: 'Label for the "month" interval.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'month',
  },
  {
    path: 'variables.labels.yearText',
    type: 'text',
    label: 'Year',
    description: 'Label for the "year" interval.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'year',
  },
  {
    path: 'variables.labels.recurringText',
    type: 'text',
    label: 'Recurring',
    description:
      'Fallback interval label on a recurring variant option when no specific cadence applies (e.g. "+$5 / recurring" while a one-time price is selected).',
    category: 'labels',
    cssVars: [],
    defaultValue: 'recurring',
  },
  {
    path: 'variables.labels.trialDaysFreeText',
    type: 'text',
    label: 'Trial days free',
    description: 'Label for trial days (e.g. "7 days free").',
    category: 'labels',
    cssVars: [],
    defaultValue: 'days free',
  },
  {
    path: 'variables.labels.trialThenText',
    type: 'text',
    label: 'Trial then',
    description: 'Label between trial and price (e.g. "then $10/month").',
    category: 'labels',
    cssVars: [],
    defaultValue: 'then',
  },
  {
    path: 'variables.labels.setupFeeText',
    type: 'text',
    label: 'Setup fee',
    description: 'Label for the setup fee on the product card.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'setup fee',
  },
  {
    path: 'variables.labels.cartSetupFeeText',
    type: 'text',
    label: 'Price breakdown setup fee',
    description: 'Label for the setup fee in the price breakdown.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Setup fee',
  },
  {
    path: 'variables.labels.startsOnText',
    type: 'text',
    label: 'Starts on',
    description: 'Label for subscription start date.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Starts on',
  },
  {
    path: 'variables.labels.renewsOnText',
    type: 'text',
    label: 'Renews on',
    description: 'Label for subscription renewal date.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Renews on',
  },
  {
    path: 'variables.labels.todayText',
    type: 'text',
    label: 'Today',
    description: 'Label for "today" date display.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Today',
  },
  {
    path: 'variables.labels.proratedAmountText',
    type: 'text',
    label: 'Prorated amount',
    description: 'Label for prorated amount display.',
    category: 'labels',
    cssVars: [],
    defaultValue: '(prorated amount)',
  },
  {
    path: 'variables.labels.endsAfterText',
    type: 'text',
    label: 'Ends after',
    description: 'Label for subscription end (e.g. "Ends after 12 payments").',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Ends after',
  },
  {
    path: 'variables.labels.endsAfterPaymentsText',
    type: 'text',
    label: 'Ends after payments',
    description: 'Label for payment count (e.g. "Ends after 12 payments").',
    category: 'labels',
    cssVars: [],
    defaultValue: 'payments',
  },
  {
    path: 'variables.labels.variantOptionTotalText',
    type: 'text',
    label: 'Variant option total',
    description: 'Label for variant option total.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Total',
  },

  // Event labels
  {
    path: 'variables.labels.ticketsRemainingText',
    type: 'text',
    label: 'Tickets remaining',
    description: 'Label for remaining ticket count.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'remaining',
  },
  {
    path: 'variables.labels.ticketTypeTotalText',
    type: 'text',
    label: 'Ticket type total',
    description: 'Label for ticket type total.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Total',
  },
  {
    path: 'variables.labels.bookingFeeText',
    type: 'text',
    label: 'Booking fee',
    description: 'Name of the per-ticket booking fee, on the ticket and in the price breakdown.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Booking fee',
  },
  {
    path: 'variables.labels.ticketFeeIncludesText',
    type: 'text',
    label: 'Ticket fee included',
    description:
      'Leads the note under a ticket price that includes its fee, e.g. "Includes $3 booking fee".',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Includes',
  },

  {
    path: 'variables.labels.canceledText',
    type: 'text',
    label: 'Canceled',
    description: 'Label for canceled status.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Canceled',
  },
  {
    path: 'variables.labels.canceledButtonText',
    type: 'text',
    label: 'Canceled button',
    description: 'Label for canceled button.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Canceled',
  },
  {
    path: 'variables.labels.unavailableText',
    type: 'text',
    label: 'Unavailable',
    description: 'Label for unavailable status.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Unavailable',
  },
  {
    path: 'variables.labels.onSaleSoonText',
    type: 'text',
    label: 'On sale soon',
    description: 'Label for upcoming sale status.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'On sale soon',
  },
  {
    path: 'variables.labels.notOnSaleText',
    type: 'text',
    label: 'Not on sale',
    description: 'Label for not-on-sale status.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Not on sale',
  },
  {
    path: 'variables.labels.startDateText',
    type: 'text',
    label: 'Start date',
    description: 'Label for event start date.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Starts',
  },
  {
    path: 'variables.labels.endDateText',
    type: 'text',
    label: 'End date',
    description: 'Label for event end date.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Ends',
  },
  {
    path: 'variables.labels.endedAtDateText',
    type: 'text',
    label: 'Ended on',
    description: 'Label for past event end date.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Ended on',
  },
  {
    path: 'variables.labels.virtualEventText',
    type: 'text',
    label: 'Virtual event',
    description: 'Label for virtual event type.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Virtual event',
  },
  {
    path: 'variables.labels.hybridEventText',
    type: 'text',
    label: 'Hybrid event',
    description: 'Label for hybrid event type.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Hybrid event',
  },

  // Checkout form labels
  {
    path: 'variables.labels.soldOutText',
    type: 'text',
    label: 'Sold out',
    description: 'Label for sold out status.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Sold out',
  },
  {
    path: 'variables.labels.soldOutButtonText',
    type: 'text',
    label: 'Sold out button',
    description: 'Label for sold out button.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Sold out',
  },
  {
    path: 'variables.labels.customerInformationText',
    type: 'text',
    label: 'Customer information',
    description: 'Label for customer information section.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Customer information',
  },
  {
    path: 'variables.labels.emailSuggestionText',
    type: 'text',
    label: 'Did you mean',
    description: 'Label for email typo suggestion.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Did you mean',
  },
  {
    path: 'variables.labels.addDiscountCodeText',
    type: 'text',
    label: 'Add discount code',
    description: 'Label for add discount code link.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Add discount code',
  },
  {
    path: 'variables.labels.discountCodeText',
    type: 'text',
    label: 'Discount code',
    description: 'Label for discount code field.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Discount code',
  },
  {
    path: 'variables.labels.discountCodePlaceholderText',
    type: 'text',
    label: 'Discount code placeholder',
    description: 'Placeholder for discount code input.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Discount code',
  },
  {
    path: 'variables.labels.discountCodeApplyText',
    type: 'text',
    label: 'Discount code apply',
    description: 'Label for discount code apply button.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Apply',
  },
  {
    path: 'variables.labels.payWhatYouWantText',
    type: 'text',
    label: 'Pay what you want',
    description: 'Label for pay what you want field.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Pay what you want',
  },
  {
    path: 'variables.labels.payWhatYouWantPlaceholderText',
    type: 'text',
    label: 'Pay what you want placeholder',
    description:
      'Placeholder for pay what you want input (shown when no minimum or suggested price is set).',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Enter amount',
  },
  {
    path: 'variables.labels.pwywMinPricePlaceholderText',
    type: 'text',
    label: 'PWYW min. price placeholder',
    description: 'Placeholder for PWYW minimum price input.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Min.',
  },
  {
    path: 'variables.labels.pwywSuggestedPricePlaceholderText',
    type: 'text',
    label: 'PWYW suggested price placeholder',
    description: 'Placeholder for PWYW suggested price input.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Suggested',
  },
  {
    path: 'variables.labels.ticketPwywMinPricePlaceholderText',
    type: 'text',
    label: 'Ticket PWYW min. price placeholder',
    description:
      'Placeholder label for the pay-what-you-want ticket amount input when no suggested price is set.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Min.',
  },
  {
    path: 'variables.labels.ticketPwywSuggestedPricePlaceholderText',
    type: 'text',
    label: 'Ticket PWYW suggested price placeholder',
    description:
      'Placeholder label for the pay-what-you-want ticket amount input when a suggested price is set.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Suggested',
  },
  {
    path: 'variables.labels.ticketPwywEnterAmountPlaceholderText',
    type: 'text',
    label: 'Ticket PWYW enter amount placeholder',
    description:
      'Placeholder for the pay-what-you-want ticket amount input when neither a minimum nor a suggested price is set.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Enter amount',
  },
  {
    path: 'variables.labels.pwywEnterAmountText',
    type: 'text',
    label: 'PWYW enter amount',
    description: 'Label for PWYW options without a minimum or suggested price.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Enter amount',
  },
  {
    path: 'variables.labels.orPayWithCardText',
    type: 'text',
    label: 'Or pay with card',
    description: 'Label for alternative card payment option.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Or pay with card',
  },
  {
    path: 'variables.labels.cardLabelText',
    type: 'text',
    label: 'Card label',
    description: 'Label for card payment field.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Card',
  },
  {
    path: 'variables.labels.variantIsRequiredText',
    type: 'text',
    label: 'Variant is required',
    description: 'Validation message for required variants.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'is required',
  },

  // Price breakdown labels
  {
    path: 'variables.labels.cartSubtotalText',
    type: 'text',
    label: 'Price breakdown subtotal',
    description: 'Label for subtotal in price breakdown.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Subtotal',
  },
  {
    path: 'variables.labels.orderBumpsTitleText',
    type: 'text',
    label: 'Order bumps heading',
    description: 'Heading above the order bump cards.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Add to your order',
  },
  {
    path: 'variables.labels.orderBumpCallToActionText',
    type: 'text',
    label: 'Order bump call to action',
    description: 'Button or link copy on an order bump that has none of its own.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Add',
  },
  {
    path: 'variables.labels.orderBumpShowMoreText',
    type: 'text',
    label: 'Order bump show more',
    description: 'Toggle that expands a collapsed order bump description.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Show more',
  },
  {
    path: 'variables.labels.showMoreText',
    type: 'text',
    label: 'Show more',
    description: 'Toggle that expands a description cut to its first lines.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Show more',
  },
  {
    path: 'variables.labels.orderBumpShowLessText',
    type: 'text',
    label: 'Order bump show less',
    description: 'Toggle that collapses an expanded order bump description.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Show less',
  },
  {
    path: 'variables.labels.showLessText',
    type: 'text',
    label: 'Show less',
    description: 'Toggle that collapses an expanded description.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Show less',
  },
  {
    path: 'variables.labels.cartBulkDiscountText',
    type: 'text',
    label: 'Price breakdown bulk discount',
    description: 'Label for bulk discount in price breakdown.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Bulk discount',
  },
  {
    path: 'variables.labels.cartCouponDiscountText',
    type: 'text',
    label: 'Price breakdown coupon discount',
    description: 'Label for coupon discount in price breakdown.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Coupon discount',
  },
  {
    path: 'variables.labels.cartTaxText',
    type: 'text',
    label: 'Price breakdown tax',
    description: 'Label for tax in price breakdown.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Tax',
  },
  {
    path: 'variables.labels.cartTaxIncludedText',
    type: 'text',
    label: 'Price breakdown tax included',
    description: 'Leads the tax line when prices include tax, e.g. "Includes VAT (20%)".',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Includes',
  },
  {
    path: 'variables.labels.processingFeeContributionText',
    type: 'text',
    label: 'Price breakdown processing fee contribution',
    description:
      'Name of the processing fee contribution line in the price breakdown, added when the customer ticks to cover processing fees.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Processing fee contribution',
  },
  {
    path: 'variables.labels.cartTotalOnStartDateText',
    type: 'text',
    label: 'Price breakdown total on start date',
    description: 'Label for total on start date in price breakdown.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Total on',
  },
  {
    path: 'variables.labels.cartTotalAfterTrialText',
    type: 'text',
    label: 'Price breakdown total after trial',
    description: 'Label for total after trial in price breakdown.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Total after trial',
  },
  {
    path: 'variables.labels.cartDueNowText',
    type: 'text',
    label: 'Price breakdown due now',
    description: 'Label for due now in price breakdown.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Due now',
  },
  {
    path: 'variables.labels.cartTotalDueText',
    type: 'text',
    label: 'Price breakdown total due',
    description: 'Label for total due in price breakdown.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Total due',
  },
  {
    path: 'variables.labels.cartTotalDueProratedAmountText',
    type: 'text',
    label: 'Price breakdown total due (prorated)',
    description: 'Label for prorated total due in price breakdown.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Total due now (prorated)',
  },

  // Button labels
  {
    path: 'variables.labels.payButtonText',
    type: 'text',
    label: 'Pay button',
    description: 'Label for pay button.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Pay',
  },
  {
    path: 'variables.labels.subscribeButtonText',
    type: 'text',
    label: 'Subscribe button',
    description: 'Label for subscribe button.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Subscribe',
  },
  {
    path: 'variables.labels.submitButtonText',
    type: 'text',
    label: 'Submit button',
    description: 'Label for submit button.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Submit',
  },
  {
    path: 'variables.labels.getTicketsButtonText',
    type: 'text',
    label: 'Get tickets button',
    description: 'Label for get tickets button.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Get tickets',
  },

  // Other labels
  {
    path: 'variables.labels.securePaymentsText',
    type: 'text',
    label: 'Secure payments',
    description: 'Label for secure payments trust badge.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Payments are secure & encrypted',
  },
  {
    path: 'variables.labels.scanToOpenText',
    type: 'text',
    label: 'Scan to open',
    description: 'Label for QR pages that open a form.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Scan to open',
  },
  {
    path: 'variables.labels.scanToPayText',
    type: 'text',
    label: 'Scan to pay',
    description: 'Label for QR pages that open a payment page.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Scan to pay',
  },
  {
    path: 'variables.labels.upsellNoThanksText',
    type: 'text',
    label: 'Upsell no thanks',
    description: 'Label for upsell decline link.',
    category: 'labels',
    cssVars: [],
    defaultValue: "No thanks, I don't want this",
  },
  {
    path: 'variables.labels.processingPaymentText',
    type: 'text',
    label: 'Processing payment',
    description: 'Label shown while processing payment.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Processing payment',
  },
  {
    path: 'variables.labels.viewPaymentHistoryText',
    type: 'text',
    label: 'View payment history',
    description: 'Label for payment history link.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'View payment history',
  },
  {
    path: 'variables.labels.viewSubmissionHistoryText',
    type: 'text',
    label: 'View submission history',
    description: 'Label for submission history link.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'View submission history',
  },
  {
    path: 'variables.labels.viewDownloadsText',
    type: 'text',
    label: 'View downloads',
    description: 'Label for downloads link.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'View downloads',
  },
  {
    path: 'variables.labels.ticketsPurchasedText',
    type: 'text',
    label: 'Tickets purchased',
    description: 'Label for tickets purchased confirmation.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Tickets purchased',
  },
  {
    path: 'variables.labels.addToCalendarText',
    type: 'text',
    label: 'Add to calendar',
    description: 'Label for the add to calendar button on the event confirmation.',
    category: 'labels',
    cssVars: [],
    defaultValue: 'Add to calendar',
  },
];

// ============================================================================
// PricePicker entries
// ============================================================================

const PRICE_PICKER_ENTRIES: ThemeProperty[] = [
  {
    path: 'variables.elements.product.pricePicker.position',
    type: 'select',
    label: 'Position',
    description: 'Where the price options are displayed.',
    category: 'product',
    cssVars: [],
    options: [
      { value: 'content', label: 'Content' },
      { value: 'form', label: 'Form' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.groupBy',
    type: 'select',
    label: 'Price tabs',
    description: 'How price options are grouped into tabs.',
    category: 'product',
    cssVars: [],
    defaultValue: 'auto',
    options: [
      { value: 'auto', label: 'Auto' },
      { value: 'none', label: 'None' },
      { value: 'billing', label: 'Billing model' },
      { value: 'interval', label: 'Interval' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.groupingMode',
    type: 'select',
    label: 'Tabs layout',
    description: 'Direction of the grouping tabs bar.',
    category: 'product',
    cssVars: [],
    options: [
      { value: 'horizontal', label: 'Horizontal' },
      { value: 'vertical', label: 'Vertical' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.layout',
    type: 'select',
    label: 'Layout',
    description: 'Price options as a list or a grid.',
    category: 'product',
    cssVars: [],
    options: [
      { value: 'list', label: 'List' },
      { value: 'grid-2', label: '2 columns' },
      { value: 'grid-3', label: '3 columns' },
      { value: 'grid-4', label: '4 columns' },
      { value: 'grid-5', label: '5 columns' },
      { value: 'grid-6', label: '6 columns' },
      { value: 'grid-7', label: '7 columns' },
      { value: 'grid-8', label: '8 columns' },
      { value: 'grid-9', label: '9 columns' },
      { value: 'grid-10', label: '10 columns' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.horizontalAlign',
    type: 'select',
    label: 'Horizontal alignment',
    description: 'Horizontal alignment of the price option content.',
    category: 'product',
    cssVars: [],
    options: [
      { value: 'left', label: 'Left' },
      { value: 'center', label: 'Center' },
      { value: 'right', label: 'Right' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.verticalAlign',
    type: 'select',
    label: 'Vertical alignment',
    description: 'Vertical alignment of the price option content.',
    category: 'product',
    cssVars: [],
    options: [
      { value: 'top', label: 'Top' },
      { value: 'center', label: 'Center' },
      { value: 'bottom', label: 'Bottom' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.pwywDisplay',
    type: 'select',
    label: 'Pay what you want display',
    description: 'How pay-what-you-want prices are displayed in the price list.',
    category: 'product',
    cssVars: [],
    options: [
      { value: 'inline', label: 'Inline input' },
      { value: 'button', label: 'Button' },
      { value: 'standalone', label: 'Standalone input' },
    ],
  },
  {
    path: 'variables.elements.product.pricePicker.styles.gap',
    cssProperty: 'gap',
    type: 'size',
    label: 'Spacing',
    description: 'Spacing between price picker options.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-gap',
        transform: 'px',
        cssDefault: '0.5rem',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Option background',
    description: 'Background color of price picker options.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Option border',
    description: 'Border color of price picker options.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Option border width',
    description: 'Border width of price picker options.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-border-width',
        transform: 'px',
        // "Cards style prices": follows the Cards border width (1px default).
        cssDefault: 'var(--cp_card-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Option border radius',
    description: 'Border radius of price picker options.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_card-border-radius)',
      },
    ],
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.styles.padding',
    cssProperty: 'padding',
    type: 'size',
    label: 'Option padding',
    description: 'Padding inside price picker options.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-padding',
        transform: 'px',
        cssDefault: '1rem',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  // --- Option-level Fill + Typography ---
  ...withSetMirrors([
    {
      path: 'variables.elements.product.pricePicker.option.styles.color',
      cssProperty: 'color',
      type: 'color',
      label: 'Text',
      description: 'Text color of all price option text (except the highlight).',
      category: 'product',
      cssVars: [
        {
          cssVar: 'price-picker-option-color',
          transform: 'direct',
          cssDefault: 'var(--cp_card-name-color)',
        },
      ],
      resetable: true,
    },
    ...fontGroup(
      'variables.elements.product.pricePicker.option.styles',
      'price-picker-option',
      'price option text',
      'product',
      {
        fontFamily: 'var(--cp_card-name-font-family)',
        fontStyle: 'var(--cp_card-name-font-style)',
        fontWeight: 'var(--cp_card-name-font-weight)',
        fontSize: 'var(--cp_card-name-font-size)',
        lineHeight: 'inherit',
      },
    ),
  ]),
  // --- Selected state ---
  {
    path: 'variables.elements.product.pricePicker.option.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Background color of the selected price picker option.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description: 'Border color of the selected price picker option.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-border-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_card-border-color-selected)',
      },
    ],
    resetable: true,
  },
  // --- Indicator ---
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Shape color',
    description: 'Color of the selected mark (dot, tick or star).',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-indicator-color',
        transform: 'direct',
        cssDefault: 'var(--cp_color-secondary)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Indicator background',
    description: 'Background color of the price picker indicator circle.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-indicator-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Indicator border',
    description: 'Border color of the price picker indicator circle.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-indicator-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_price-picker-option-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.size',
    cssProperty: 'width',
    type: 'size',
    label: 'Indicator size',
    description: 'Width and height of the price picker selection indicator.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-indicator-size',
        transform: 'px',
        cssDefault: '16px',
      },
    ],
    options: 'indicatorSize' as OptionsRef,
    defaultValue: 16,
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Indicator border width',
    description: 'Border width of the price picker indicator circle.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-indicator-border-width',
        transform: 'px',
        cssDefault: '2px',
      },
    ],
    options: 'borderWidth' as OptionsRef,
    defaultValue: 2,
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.shape',
    type: 'select',
    label: 'Shape',
    description: 'Shape of the selected price picker mark (dot, tick or star).',
    category: 'product',
    cssVars: [],
    options: 'indicatorShape' as OptionsRef,
    defaultValue: 'dot',
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Border radius',
    description: 'Border radius of the price picker indicator ring (full = circle, none = square).',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-indicator-border-radius',
        transform: 'px',
        cssDefault: '9999px',
      },
    ],
    options: 'indicatorRadius' as OptionsRef,
    defaultValue: 9999,
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected background',
    description: 'Background (fill) color of the selected price picker indicator circle.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-indicator-background-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_card-background-color-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.indicator.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected border',
    description: 'Border (ring) color of the selected price picker indicator circle.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-indicator-border-color-selected',
        transform: 'direct',
        cssDefault: 'var(--cp_price-picker-option-border-color-selected)',
      },
    ],
    resetable: true,
  },
  // --- Option badge ---
  // The pill is the Cards highlight until a seller styles it apart, so Cards >
  // Highlight restyles it (and, through it, the order bump pill).
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Highlight text',
    description: 'Text color of the price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-badge-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-badge-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Highlight background',
    description: 'Background color of the price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-badge-background-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-badge-background-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Highlight border',
    description: 'Border color of the price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-badge-border-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-badge-border-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Highlight border width',
    description: 'Border width of the price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-badge-border-width',
        transform: 'px',
        cssDefault: 'var(--cp_card-badge-border-width)',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Highlight border radius',
    description: 'Border radius of the price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-badge-border-radius',
        transform: 'px',
        cssDefault: 'var(--cp_card-badge-border-radius)',
      },
    ],
    options: 'badgeRadius' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.option.badge.styles.padding',
    cssProperty: 'padding',
    type: 'size',
    label: 'Highlight padding',
    description: 'Padding inside the price highlight.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-badge-padding',
        transform: 'px',
        cssDefault: 'var(--cp_card-badge-padding)',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  ...fontGroup(
    'variables.elements.product.pricePicker.option.badge.styles',
    'price-picker-option-badge',
    'price highlight',
    'product',
    {
      fontFamily: 'var(--cp_card-badge-font-family)',
      fontStyle: 'var(--cp_card-badge-font-style)',
      fontWeight: 'var(--cp_card-badge-font-weight)',
      fontSize: 'var(--cp_card-badge-font-size)',
      lineHeight: 'var(--cp_card-badge-line-height)',
    },
  ),
  // --- Option pwywInput (the multi-price pay-what-you-want amount box) ---
  ...pwywInputGroup(
    'variables.elements.product.pricePicker.option.pwywInput',
    'price-picker-option-pwywInput',
    { chainTo: 'pwyw-input' },
  ),
  ...pwywSuffixGroup(
    'variables.elements.product.pricePicker.option.pwywSuffix',
    'price-picker-option-pwywSuffix',
    'product',
  ),
  // --- Option pwywLabel ---
  ...pwywLabelGroup(
    'variables.elements.product.pricePicker.option.pwywLabel',
    'price-picker-option-pwywLabel',
    {
      category: 'product',
      chainTo: 'pwyw-label',
      setTiers: ['price-picker-option-label', 'checkout-form-fields-label'],
      setTiersOverride: {
        paddingBottom: [],
      },
      fallback: {
        color: 'var(--cp_price-picker-option-label-color)',
        paddingBottom: 'var(--cp_checkout-form-fields-label-padding-bottom)',
        fontFamily: 'var(--cp_price-picker-option-label-font-family)',
        fontStyle: 'var(--cp_price-picker-option-label-font-style)',
        fontWeight: 'var(--cp_price-picker-option-label-font-weight)',
        fontSize: 'var(--cp_price-picker-option-label-font-size)',
        lineHeight: 'var(--cp_price-picker-option-label-line-height)',
      },
    },
  ),
  // --- Grouping tabs (the One-time / Subscription switcher) ---
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Tabs background',
    description: 'Background color of the price grouping tabs bar.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-tabs-background-color',
        transform: 'direct',
        // The segmented-control track: a dedicated grey with contrast in both
        // modes. Deliberately NOT the card background (white-on-white) and
        // NOT surface-accent (the page-background grey — invisible on white
        // content and identical to the content surface on dark themes).
        cssDefault: 'var(--cp_tabs-track-background)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.selectedStyles.backgroundColor',
    cssProperty: 'backgroundColor',
    type: 'color',
    label: 'Selected tab background',
    description: 'Background color of the selected grouping tab.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-tabs-background-color-selected',
        transform: 'direct',
        // The active tab uses the neutral selected-surface token (white by
        // default), independent of Cards design settings.
        cssDefault: 'var(--cp_surface-selected)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Tabs border',
    description: 'Border color of the price grouping tabs bar.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-tabs-border-color',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.selectedStyles.borderColor',
    cssProperty: 'borderColor',
    type: 'color',
    label: 'Selected tab border',
    description: 'Border color of the selected grouping tab.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-tabs-border-color-selected',
        transform: 'direct',
        cssDefault: 'transparent',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.borderWidth',
    cssProperty: 'borderWidth',
    type: 'size',
    label: 'Tabs border width',
    description: 'Border width of the price grouping tabs bar.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-tabs-border-width',
        transform: 'px',
        cssDefault: '0px',
      },
    ],
    options: 'borderWidth' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.borderRadius',
    cssProperty: 'borderRadius',
    type: 'size',
    label: 'Tabs border radius',
    description: 'Border radius of the price grouping tabs bar.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-tabs-border-radius',
        transform: 'px',
        // Fixed segmented-control radius, independent of Cards / global radius.
        cssDefault: '0.5rem',
      },
    ],
    options: 'sizePx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.paddingBottom',
    cssProperty: 'paddingBottom',
    type: 'size',
    label: 'Tabs padding bottom',
    description: 'Space between the grouping tabs and the price options.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-tabs-padding-bottom',
        transform: 'px',
        cssDefault: '0.75rem',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  {
    path: 'variables.elements.product.pricePicker.tabs.styles.padding',
    cssProperty: 'padding',
    type: 'size',
    label: 'Tabs padding',
    description: 'Padding inside the price grouping tabs bar.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-tabs-padding',
        transform: 'px',
        cssDefault: '0.25rem',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  // --- Tab name (the grouping tab titles) ---
  // Defaults use the shared text/body tokens directly, independent of Cards
  // design settings.
  {
    path: 'variables.elements.product.pricePicker.tabs.name.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Tab name',
    description: 'Text color of the price grouping tab titles.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-tabs-name-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-primary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricePicker.tabs.name.styles',
    'price-picker-tabs-name',
    'tab name',
    'product',
    {
      fontFamily: 'var(--cp_font-body)',
      fontStyle: 'var(--cp_font-body-style)',
      fontWeight: 'calc(var(--cp_font-body-weight) + 200)',
      fontSize: 'var(--cp_font-body-size)',
      lineHeight: 'inherit',
    },
  ),
  // --- Heading + description above the options (the product's pricePicker
  // title / description). The heading follows the Cards group name; the
  // description is plain secondary text, so Cards > Detail never restyles it.
  {
    path: 'variables.elements.product.pricePicker.title.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Heading text',
    description: 'Text color of the heading above the price options.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-title-color',
        transform: 'direct',
        cssDefault: 'var(--cp_card-groupName-color)',
      },
    ],
    resetable: true,
  },
  {
    path: 'variables.elements.product.pricePicker.title.styles.paddingBottom',
    cssProperty: 'paddingBottom',
    type: 'size',
    label: 'Padding bottom',
    description: 'Space between the heading (and its description) and the price options.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-title-padding-bottom',
        transform: 'px',
        // Follows the Cards group name, but sits a step wider: the heading
        // carries a description under it and the options are boxed cards.
        cssDefault: 'calc(var(--cp_card-groupName-padding-bottom) + 0.25rem)',
      },
    ],
    options: 'paddingPx' as OptionsRef,
  },
  ...fontGroup(
    'variables.elements.product.pricePicker.title.styles',
    'price-picker-title',
    'price options heading',
    'product',
    // Follows the Cards group name, like the variants heading, so one Cards
    // change restyles every group heading together.
    {
      fontFamily: 'var(--cp_card-groupName-font-family)',
      fontStyle: 'var(--cp_card-groupName-font-style)',
      fontWeight: 'var(--cp_card-groupName-font-weight)',
      fontSize: 'var(--cp_card-groupName-font-size)',
    },
  ),
  {
    path: 'variables.elements.product.pricePicker.description.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Description text',
    description: 'Text color of the description above the price options.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-description-color',
        transform: 'direct',
        cssDefault: 'var(--cp_text-secondary)',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricePicker.description.styles',
    'price-picker-description',
    'price options description',
    'product',
    {
      fontFamily: 'var(--cp_font-body)',
      fontStyle: 'var(--cp_font-body-style)',
      fontWeight: 'var(--cp_font-body-weight)',
      fontSize: 'var(--cp_font-body-size)',
      lineHeight: 'inherit',
    },
  ),
  // --- Option label + description ---
  ...withSetMirrors([
    {
      path: 'variables.elements.product.pricePicker.option.label.styles.color',
      cssProperty: 'color',
      type: 'color',
      label: 'Label text',
      description: 'Text color of the price option label.',
      category: 'product',
      cssVars: [
        {
          cssVar: 'price-picker-option-label-color',
          transform: 'direct',
          cssDefault: 'var(--cp_price-picker-option-color-set, var(--cp_card-name-color))',
        },
      ],
      resetable: true,
    },
    ...fontGroup(
      'variables.elements.product.pricePicker.option.label.styles',
      'price-picker-option-label',
      'price option label',
      'product',
      {
        fontFamily:
          'var(--cp_price-picker-option-font-family-set, var(--cp_card-name-font-family))',
        fontStyle: 'var(--cp_price-picker-option-font-style-set, var(--cp_card-name-font-style))',
        fontWeight:
          'var(--cp_price-picker-option-font-weight-set, var(--cp_card-name-font-weight))',
        fontSize: 'var(--cp_price-picker-option-font-size-set, var(--cp_card-name-font-size))',
        lineHeight: 'var(--cp_price-picker-option-line-height-set, inherit)',
      },
    ),
  ]),
  ...withSetMirrors([
    {
      path: 'variables.elements.product.pricePicker.option.description.styles.color',
      cssProperty: 'color',
      type: 'color',
      label: 'Description text',
      description: 'Text color of the price option description.',
      category: 'product',
      cssVars: [
        {
          cssVar: 'price-picker-option-description-color',
          transform: 'direct',
          cssDefault: 'var(--cp_price-picker-option-color-set, var(--cp_card-description-color))',
        },
      ],
      resetable: true,
    },
    ...fontGroup(
      'variables.elements.product.pricePicker.option.description.styles',
      'price-picker-option-description',
      'price option description',
      'product',
      {
        fontFamily:
          'var(--cp_price-picker-option-font-family-set, var(--cp_card-description-font-family))',
        fontStyle:
          'var(--cp_price-picker-option-font-style-set, var(--cp_card-description-font-style))',
        fontWeight:
          'var(--cp_price-picker-option-font-weight-set, var(--cp_card-description-font-weight))',
        fontSize:
          'var(--cp_price-picker-option-font-size-set, var(--cp_card-description-font-size))',
        lineHeight: 'var(--cp_price-picker-option-line-height-set, inherit)',
      },
    ),
  ]),
  // ==========================================================================
  // Price options — per-option price + detail lines (the "Price options"
  // element). These are independent of the single-price "Prices" element
  // (product.pricing.*): the option lines sit on the card surface, so their
  // defaults follow the card price / the option description rather than the
  // product/panel background. The single-price block keeps product.pricing.*.
  // ==========================================================================
  // --- Option price (the amount) ---
  {
    path: 'variables.elements.product.pricePicker.option.price.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Price',
    description: 'Color of the price shown in each price option.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-price-color',
        transform: 'direct',
        // Explicit option-level Fill first (set-only mirror), else "Cards
        // style prices": the option price follows the card price colour.
        cssDefault: 'var(--cp_price-picker-option-color-set, var(--cp_card-price-color))',
      },
    ],
    resetable: true,
  },
  // Explicit option-level Typography first (set-only mirrors), else the card
  // PRICE's text style.
  ...fontGroup(
    'variables.elements.product.pricePicker.option.price.styles',
    'price-picker-option-price',
    'price option price',
    'product',
    {
      fontFamily: 'var(--cp_price-picker-option-font-family-set, var(--cp_card-price-font-family))',
      fontStyle: 'var(--cp_price-picker-option-font-style-set, var(--cp_card-price-font-style))',
      fontWeight: 'var(--cp_price-picker-option-font-weight-set, var(--cp_card-price-font-weight))',
      fontSize: 'var(--cp_price-picker-option-font-size-set, var(--cp_card-price-font-size))',
      lineHeight: 'var(--cp_price-picker-option-line-height-set, inherit)',
    },
  ),
  // --- Option interval (the "/ month") — its OWN element, independent of the
  // --- single-price block's interval. Defaults to `inherit` so it matches the
  // --- surrounding option price until a seller styles it separately.
  {
    path: 'variables.elements.product.pricePicker.option.interval.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Interval',
    description: 'Color of the subscription interval (e.g. "/ month") in each price option.',
    category: 'product',
    cssVars: [
      { cssVar: 'price-picker-option-interval-color', transform: 'direct', cssDefault: 'inherit' },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricePicker.option.interval.styles',
    'price-picker-option-interval',
    'price option interval',
    'product',
    {
      fontFamily: 'inherit',
      fontStyle: 'inherit',
      fontWeight: 'inherit',
      fontSize: 'inherit',
      lineHeight: 'inherit',
    },
  ),
  // --- Option discounted-from (struck-through original price) ---
  {
    path: 'variables.elements.product.pricePicker.option.discountedFromPrice.styles.color',
    cssProperty: 'color',
    type: 'color',
    label: 'Compare-at price',
    description: 'Color of the original price shown struck-through in a price option.',
    category: 'product',
    cssVars: [
      {
        cssVar: 'price-picker-option-discounted-from-color',
        transform: 'direct',
        cssDefault:
          'var(--cp_price-picker-option-color-set, var(--cp_card-discountedFromPrice-color))',
      },
    ],
    resetable: true,
  },
  ...fontGroup(
    'variables.elements.product.pricePicker.option.discountedFromPrice.styles',
    'price-picker-option-discounted-from',
    'price option compare-at price',
    'product',
    {
      fontFamily:
        'var(--cp_price-picker-option-font-family-set, var(--cp_card-discountedFromPrice-font-family))',
      fontStyle:
        'var(--cp_price-picker-option-font-style-set, var(--cp_card-discountedFromPrice-font-style))',
      fontWeight:
        'var(--cp_price-picker-option-font-weight-set, var(--cp_card-discountedFromPrice-font-weight))',
      fontSize:
        'var(--cp_price-picker-option-font-size-set, var(--cp_card-discountedFromPrice-font-size))',
      lineHeight: 'var(--cp_price-picker-option-line-height-set, inherit)',
    },
  ),
  ...optionDetailLine('setupFee', 'price-picker-option-setup-fee', 'Setup fee', 'setup fee'),
  ...optionDetailLine('startDate', 'price-picker-option-starts-on', 'Start date', 'start date'),
  ...optionDetailLine(
    'planIterations',
    'price-picker-option-ends-after',
    'Number of payments',
    'number of payments',
  ),
  ...optionDetailLine('trialPeriod', 'price-picker-option-trial', 'Trial period', 'trial period'),
  ...optionDetailLine(
    'prorationRenews',
    'price-picker-option-proration-renews',
    'Proration renews',
    'proration renews',
  ),
  ...optionDetailLine(
    'prorationAmount',
    'price-picker-option-proration-amount',
    'Proration amount',
    'proration amount',
  ),
];

// ============================================================================
// Exported registry
// ============================================================================

export const THEME_PROPERTY_REGISTRY: ThemeProperty[] = [
  ...TOKEN_ENTRIES,
  ...LAYOUT_ENTRIES,
  ...PRODUCT_ENTRIES,
  ...DESCRIPTION_ENTRIES,
  ...CHECKOUT_ENTRIES,
  ...FIELD_ENTRIES,
  ...BUTTON_ENTRIES,
  ...CARD_ENTRIES,
  ...VARIANT_ENTRIES,
  ...STATUS_BADGE_ENTRIES,
  ...TICKET_GROUP_ENTRIES,
  ...DISCOUNT_CODE_ENTRIES,
  ...PAYMENT_ENTRIES,
  ...PRICE_BREAKDOWN_ENTRIES,
  ...COVER_PROCESSING_FEES_ENTRIES,
  ...ORDER_BUMP_ENTRIES,
  ...TRUST_ENTRIES,
  ...CONFIRMATION_ENTRIES,
  ...PWYW_ENTRIES,
  ...TOGGLE_ENTRIES,
  ...NON_STYLE_ENTRIES,
  ...LABEL_ENTRIES,
  ...PRICE_PICKER_ENTRIES,
];

// ============================================================================
// Lookup helpers
// ============================================================================

let _byPath: Map<string, ThemeProperty> | null = null;

function ensurePathMap(): Map<string, ThemeProperty> {
  if (!_byPath) {
    _byPath = new Map();
    for (const prop of THEME_PROPERTY_REGISTRY) {
      _byPath.set(prop.path, prop);
    }
  }
  return _byPath;
}

export function getCssDefaultDeclarations(): string[] {
  return THEME_PROPERTY_REGISTRY.flatMap(prop =>
    prop.cssVars.flatMap(mapping =>
      mapping.cssDefault ? [`--cp_${mapping.cssVar}: ${mapping.cssDefault};`] : [],
    ),
  );
}

export function getPropertyByPath(path: string): ThemeProperty | undefined {
  return ensurePathMap().get(path);
}

export function getPropertiesByCategory(category: ThemePropertyCategory): ThemeProperty[] {
  return THEME_PROPERTY_REGISTRY.filter(p => p.category === category);
}

export function getPropertiesByPath(): Record<string, ThemeProperty> {
  const map = ensurePathMap();
  const result: Record<string, ThemeProperty> = {};
  for (const [key, value] of map) {
    result[key] = value;
  }
  return result;
}

/**
 * Get all label defaults as a { labelKey: defaultValue } map.
 * This is the single source of truth for label defaults — used by
 * getThemeLabels() and themeDefaults.js instead of duplicating values.
 */
let _labelDefaults: Record<string, string> | null = null;

export function getThemeLabelDefaults(): Record<string, string> {
  if (!_labelDefaults) {
    _labelDefaults = {};
    for (const prop of LABEL_ENTRIES) {
      // Extract key name from path: 'variables.labels.setupFeeText' -> 'setupFeeText'
      const key = prop.path.split('.').pop()!;
      _labelDefaults[key] = (prop.defaultValue as string) ?? '';
    }
  }
  return _labelDefaults;
}
